import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Costs } from './Costs';
import { Session } from '@supabase/supabase-js';
import { uploadFile } from './files';
import { supabase } from './supabase';
import { ensureOrderCoords } from './geocode';
import { ControlDespacho } from './ControlDespacho';
import { MapaMensajes } from './MapaMensajes';
import { AppSidebar, buildNav, STAFF_HIDDEN_TABS } from './ui/AppSidebar';
import { isOwnShipping } from './shipping';
import { RECEIPT_MIME_OR, RECEIPT_KIND_OR, STICKER_MIME, isReceiptMime } from './receipts';
import { Clock, Search, ChevronDown, CheckCircle, AlertCircle, X, Truck, MessageSquare, Info, Download, Trash2, LogOut, RefreshCw, Activity, MessageCircle, Printer, Loader2, Plus } from 'lucide-react';
import { Login } from './Login';
import { CanjeHome } from './CanjeHome';
import { TwoFactor } from './TwoFactor';
import { NewOrder } from './NewOrder';
import { generateBudgetPDF } from './pdfGenerator';
import { Notes } from './Notes';
import { ActivityLog } from './ActivityLog';
import { logActivity } from './logger';
import { readOrdersHistory, writeOrdersHistory } from './ordersCache';
import WhatsAppTest from './WhatsAppTest';
import { Inbox as InboxView } from './Inbox';
import { Nesting } from './Nesting';
import { Taller } from './Taller';
import { Insumos } from './Insumos';
import { Products } from './Products';
import PrintOrdersTool from './PrintOrdersTool';
import { generateCompactThermalLabelsPDF } from './pdfGenerator';
import { MeliListings } from './MeliListings';
import Imports from './Imports';
import CirculoRojo from './CirculoRojo';
import Reviews from './Reviews';
import AbandonedCarts from './AbandonedCarts';
import Consignment from './Consignment';
import PushBell from './PushBell';
import CabifyBatch, { cabifyQualifies, CabifyStatusModal, CabifyScreen, CABIFY_STATE_ES, CABIFY_DEAD } from './CabifyBatch'; // MÓDULO CABIFY (encapsulado)
import Dhl from './Dhl';
import Wholesale from './Wholesale';
import { waTemplateDisplay } from './waTemplates';
import { resolveTemplateName } from './whatsappTemplates';
import './App.css';

type Order = {
  id: number;
  tiendanube_id: string;
  meli_order_id?: number;
  contact_name: string;
  created_at: string;
  products: any;
  status: string;
  shipping_option?: string;
  shipping_pickup_details?: string;
  payment_status?: string;
  gateway_name?: string;
  note?: string;
  owner_note?: string;
  source?: string;
  contact_phone?: string;
  discount?: number;
  delivery_type?: 'envio' | 'retiro';
  delivery_address?: string;
  delivery_unit?: string | null;   // piso/depto aparte (migr 099)
  coupon_code?: string | null;
  coupon_amount?: number | null;
  payment_details?: {
    method?: string;
    [key: string]: any;
  };
  total_price?: number;
  shipping_cost_customer?: number;
  shipping_cost_owner?: number;
  deleted_at?: string | null;
  edited_locally?: boolean;
  carrier_status?: string | null;     // envío por correo según TN (migr 113)
  carrier_shipped_at?: string | null;
  edited_at?: string | null;
  needs_payment_followup?: boolean;
  payment_followup_at?: string | null;
  // Fecha de LISTO (empaquetado) y prioridad del taller — migr 098
  ready_by?: string | null;
  ready_by_kind?: 'auto' | 'promised' | null;
  ready_by_original?: string | null;
  is_urgent?: boolean | null;
  delay_reason?: string | null;
};

function getOrderDisplayId(order: { source?: string; tiendanube_id?: string; id: number }): string {
  if (order.source === 'woocommerce' && order.tiendanube_id) {
    const num = order.tiendanube_id.replace(/^(WC-|BT-)/i, '');
    return `BT-${num}`;
  }
  if ((order.source === 'tiendanube' || order.source === 'mercadolibre') && order.tiendanube_id) {
    return String(order.tiendanube_id);
  }
  return `K-${order.id}`;
}

type Snackbar = {
  message: string;
  type: 'success' | 'error';
  id: number;
};

// MÓDULO CABIFY (encapsulado) — en stand-by hasta confirmar con Cabify.
// Poner en true para re-activar el botón "Cabify" en las órdenes de envío.
const CABIFY_ENABLED = true;

// 'despachado' (vuelve 22/10/2026, SOLO correo): pedidos de Andreani / Envío Nube / Punto de retiro
// que TN marcó enviados y el correo todavía no entregó (tiendanube-webhook). Cabify no lo usa:
// la orden pasa sola a 'entregado' cuando Cabify retira el paquete (cabify-webhook).
const STATUS_OPTIONS = ['nueva', 'falta_insumo', 'cortado', 'empaquetado', 'despachado', 'entregado', 'stock', 'cancelada'];

const STATUS_LABELS: Record<string, string> = {
  nueva: 'Nueva',
  falta_insumo: 'Falta Insumo',
  cortado: 'Cortado',
  empaquetado: 'Empaquetado',
  despachado: 'Despachado',
  entregado: 'Entregado',
  stock: 'Stock',
  cancelada: 'Cancelada',
};

// "Empaquetadas viejas" (29/10/2026): SOLO para los filtros/contadores de Órdenes, sin tocar el
// estado. Empaquetadas hace más de 30 días (packed_at; las de antes del 07/10 no la tienen → fecha
// del pedido): las que no retiraron / quedaron en el limbo, separadas de las empaquetadas de ahora.
const OLD_PACKED_DAYS = 30;
function isOldPacked(o: { status: string; created_at: string; packed_at?: string | null }): boolean {
  if (o.status !== 'empaquetado') return false;
  const d = new Date(o.packed_at || o.created_at).getTime();
  return Date.now() - d > OLD_PACKED_DAYS * 86400000;
}
// Estado que se usa para filtrar/contar (agrega el virtual 'empaquetado_viejo')
const filterStatusOf = (o: any): string => (isOldPacked(o) ? 'empaquetado_viejo' : o.status);
const CHIP_STATUS = ['nueva', 'falta_insumo', 'cortado', 'empaquetado', 'empaquetado_viejo', 'despachado', 'entregado', 'stock', 'cancelada'];
const CHIP_LABELS: Record<string, string> = { empaquetado_viejo: `Empaquetadas viejas (+${OLD_PACKED_DAYS} días)` };

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Detect pickup orders based on shipping_option from Tiendanube or delivery_type from manual orders
function isPickupOrder(order: Order): boolean {
  const so = (order.shipping_option || '').toLowerCase();
  if (so === 'local' || so === 'retiras en local') return true;
  if (order.delivery_type === 'retiro') return true;
  return false;
}

// Retiro en el LOCAL (o showroom manual) — el ÚNICO caso que debe recibir el
// WhatsApp 'pedido_listo_retiro'. Definición positiva (whitelist):
//   - shipping_option 'Local' / 'Local (...)' / 'Retiras en Local'  (retiro TN en el local)
//   - sin shipping_option + delivery_type 'retiro'                  (showroom manual)
// Quedan EXCLUIDOS: "Punto de retiro" del correo (Andreani a sucursal) y envíos
// a domicilio mal etiquetados como retiro (Logística Propia, Envío Gratis, etc.).
function isStorePickup(order: Order): boolean {
  const so = (order.shipping_option || '').trim().toLowerCase();
  if (so === '') return order.delivery_type === 'retiro'; // manual/showroom
  if (so === 'local' || so.startsWith('local ') || so === 'retiras en local' || so.includes('showroom')) return true;
  try {
    if (order.shipping_pickup_details) {
      const p = typeof order.shipping_pickup_details === 'string'
        ? JSON.parse(order.shipping_pickup_details)
        : order.shipping_pickup_details;
      if ((p?.name || '').trim().toLowerCase() === 'local') return true;
    }
  } catch { /* pickup details no parseable */ }
  return false;
}

// Últimos 10 dígitos del teléfono (ignora +, código 54 y el 9 de móvil) para
// matchear el mismo número guardado en formatos distintos (order vs whatsapp_logs).
function phoneTail(phone?: string | null): string {
  return (phone || '').replace(/\D/g, '').slice(-10);
}

// Transferencia "a convenir" de Tiendanube: medio de pago OFFLINE/custom que en TN
// se llama "Transferencia". Se distingue del Efectivo (mismo offline, otro nombre) y
// de la transferencia de PagoNube (gateway 'pago-nube', method 'wire_transfer').
function isTransferenciaConvenir(order: Order): boolean {
  if (order.source !== 'tiendanube') return false;
  // Solo por gateway_name: el 'Transferencia' custom/offline de TN (a convenir). NO depende
  // de payment_details.method porque ese se muta a 'other' al marcar la orden pagada.
  // PagoNube (transferencia integrada) tiene gateway_name 'Pago Nube', no matchea acá.
  return (order.gateway_name || '').toLowerCase().includes('transferencia');
}

// Detecta ítems MiPared en una orden por su SKU (MIPARED-XXXXXX) y devuelve los códigos únicos
// (ej "A7K2M9"). El despiece real se trae de la API de MiPared (edge function mipared-combo).
function getMiparedCodes(order: Order): string[] {
  const prods = Array.isArray(order?.products) ? order.products : [];
  const codes: string[] = [];
  for (const p of prods) {
    const m = String((p as any)?.sku || '').match(/MIPARED-([A-Za-z0-9]+)/i);
    if (m) codes.push(m[1].toUpperCase());
  }
  return Array.from(new Set(codes));
}

// Clasifica el medio de pago de una orden de Tiendanube en efectivo / transferencia / tarjeta.
// Prioriza payment_details.method porque PagoNube ('Pago Nube') puede ser transferencia
// (wire_transfer) O tarjeta (credit_card) con el mismo gateway_name; cae al nombre si hace falta.
function tnPaymentMethod(order: Order): 'efectivo' | 'transferencia' | 'tarjeta' | null {
  if (order.source !== 'tiendanube') return null;
  const gn = (order.gateway_name || '').toLowerCase();
  const m = order.payment_details?.method;
  if (m === 'wire_transfer') return 'transferencia';
  if (m === 'credit_card' || m === 'debit_card') return 'tarjeta';
  if (m === 'custom') return /transferencia|dep[oó]sito/.test(gn) ? 'transferencia' : 'efectivo';
  if (gn.includes('efectivo')) return 'efectivo';
  if (/transferencia|dep[oó]sito/.test(gn)) return 'transferencia';
  if (/tarjeta|cuotas|mercado\s*pago|pago\s*nube/.test(gn)) return 'tarjeta';
  return null;
}

// Un grupo de la vista wa_counts_grouped: mensajes salientes enviados de un (pedido, teléfono).
interface WaGroup { order_id: string | null; tail: string | null; n: number }

// Ruta URL de cada pantalla (deep-links + recarga sin perder la tab).
// OJO: /r/<id> está reservado en nginx para el tracking de carritos — no usar acá.
const TAB_PATHS: Record<string, string> = {
  orders: '/',
  budgets: '/presupuestos',
  products: '/productos',
  providers: '/proveedores',
  costs: '/costos',
  notes: '/notas',
  activity: '/actividad',
  whatsapp: '/whatsapp',
  transfers: '/atencion',
  print: '/etiquetas',
  meli: '/meli',
  imports: '/importaciones',
  circulo_rojo: '/circulo-rojo',
  despacho: '/control-despacho',
  mapa: '/mapa-mensajes',
  reviews: '/reviews',
  carritos: '/carritos',
  consignment: '/consignacion',
  taller: '/taller',
  insumos: '/insumos',
  wholesale: '/mayorista',
  cabify: '/cabify',
  dhl: '/dhl',
  trash: '/papelera',
};
const PATH_TABS: Record<string, string> = Object.fromEntries(
  Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab])
);

function App() {
  const [orders, setOrders] = useState<Order[]>([]);
  // Costos & BI sin canjes: total $0, no son ventas (los estadísticos de canjes salen de is_canje)
  const ordersNoCanje = useMemo(() => orders.filter(o => !(o as any).is_canje), [orders]);
  const canjeOrders = useMemo(() => orders.filter(o => (o as any).is_canje), [orders]); // Resultados: costo de lo regalado
  // Cursor del último updated_at sincronizado + debounce de los refrescos de realtime.
  // Ver el comentario de fetchOrders: bajar la tabla entera seguido nos bloqueó el proyecto.
  const lastOrdersSyncRef = useRef<string | null>(null);
  const fullLoadInFlightRef = useRef(false); // evita cargas completas superpuestas
  const refreshTimerRef = useRef<number | null>(null);
  // `loading` = sincronizando con TN/ML (botón Sincronizar). La carga desde la base va aparte:
  const [loading, setLoading] = useState(false);
  // Carga en 2 etapas (10/10/2026): primero las órdenes ABIERTAS (lo que muestra la vista por
  // defecto) y la pantalla aparece al instante; el historial (entregadas/canceladas/stock, ~95%
  // de las filas) baja después en paralelo. Costos & BI espera al historial completo.
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [filterStatus, setFilterStatus] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem('filterStatus');
    return saved ? new Set(JSON.parse(saved)) : new Set<string>();
  });
  const [filterPaymentStatus, setFilterPaymentStatus] = useState(() => sessionStorage.getItem('filterPaymentStatus') || 'all');
  const [filterDateRange, setFilterDateRange] = useState(() => sessionStorage.getItem('filterDateRange') || 'all');
  const [filterSpecificDate, setFilterSpecificDate] = useState(() => sessionStorage.getItem('filterSpecificDate') || '');
  const [sortDateOrder, setSortDateOrder] = useState(() => sessionStorage.getItem('sortDateOrder') || 'desc');
  const [filterShipping, setFilterShipping] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem('filterShipping');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [filterDeadline, setFilterDeadline] = useState(() => sessionStorage.getItem('filterDeadline') || 'all');
  // Canjes a creadores (is_canje): todas / solo canjes / sin canjes (26/10/2026)
  const [filterCanje, setFilterCanje] = useState(() => sessionStorage.getItem('filterCanje') || 'all');
  const [stockProductNames, setStockProductNames] = useState<string[]>([]);
  const [stockVariantIds, setStockVariantIds] = useState<Set<number>>(new Set());
  const [showShippingDropdown, setShowShippingDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState<'orders' | 'budgets' | 'products' | 'providers' | 'costs' | 'notes' | 'activity' | 'whatsapp' | 'transfers' | 'print' | 'meli' | 'imports' | 'circulo_rojo' | 'reviews' | 'carritos' | 'consignment' | 'taller' | 'insumos' | 'wholesale' | 'cabify' | 'despacho' | 'mapa' | 'dhl' | 'trash'>(
    // La tab inicial sale de la URL: recargar /reviews arranca EN reviews desde el primer render.
    () => (PATH_TABS[window.location.pathname.replace(/\/+$/, '') || '/'] as any) || 'orders'
  );
  const [role, setRole] = useState<'admin' | 'creator' | 'canje' | 'staff' | null>(null);
  // MÓDULO CABIFY (encapsulado): estado de etiquetas por pedido + acciones directas en la card
  const [showCabifyBatch, setShowCabifyBatch] = useState(false);
  const [cabifyMap, setCabifyMap] = useState<Map<number, { parcels: { parcel_id: string; status: string; label?: string }[]; status: string }>>(new Map());
  const [cabifyBusyId, setCabifyBusyId] = useState<number | null>(null);
  const [cabifyStatusOrder, setCabifyStatusOrder] = useState<Order | null>(null);
  // Popup de seguimiento: muestra la página pública /seguimiento tal cual la ve el cliente
  const [trackViewOrder, setTrackViewOrder] = useState<Order | null>(null);
  const [trashedOrders, setTrashedOrders] = useState<Order[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Búsqueda por ID de envío de Cabify (parcel_id, 02/11/2026): si el texto parece un id de Cabify
  // (hex con guiones, ≥ 6 caracteres y no solo números) se buscan en cabify_deliveries las órdenes
  // que tienen ese envío. Las órdenes no traen sus envíos cargados, por eso va aparte.
  const [cabifySearchIds, setCabifySearchIds] = useState<Set<number> | null>(null);
  useEffect(() => {
    const t = searchTerm.trim().toLowerCase();
    if (t.length < 6 || !/^[0-9a-f-]+$/.test(t) || /^\d+$/.test(t)) { setCabifySearchIds(null); return; }
    let alive = true;
    supabase.from('cabify_deliveries').select('order_id').ilike('parcel_id', `%${t}%`).not('order_id', 'is', null).limit(50)
      .then(({ data }) => { if (alive) setCabifySearchIds(new Set((data || []).map((d: any) => Number(d.order_id)))); });
    return () => { alive = false; };
  }, [searchTerm]);
  const [snackbars, setSnackbars] = useState<Snackbar[]>([]);
  const [notesCount, setNotesCount] = useState(0);
  const [escalatedCount, setEscalatedCount] = useState(0);
  // Cache de combos MiPared (code -> estado). El despiece se trae 1 sola vez por code (son inmutables).
  const [miparedCombos, setMiparedCombos] = useState<Record<string, { loading?: boolean; combo?: any; snapshot?: string | null; error?: string }>>({});
  const miparedFetchedRef = useRef<Set<string>>(new Set());
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [newOrderCanje, setNewOrderCanje] = useState(false); // "Nuevo canje" (rol staff)
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [tempNote, setTempNote] = useState('');
  const [showDelivered, setShowDelivered] = useState(false);
  const [openExpenseModal, setOpenExpenseModal] = useState(false);
  const [selectedPrintOrders, setSelectedPrintOrders] = useState<Set<number>>(new Set());
  const [printingSelection, setPrintingSelection] = useState(false);
  const [meliConnected, setMeliConnected] = useState(false);
  const [meliNickname, setMeliNickname] = useState('');
  const [tnConnected, setTnConnected] = useState(false);
  // hasta verificarlo, el menú no muestra "Conectar…" (antes parpadeaba en rojo al cargar)
  const [connChecked, setConnChecked] = useState(false);
  const [showTnConnect, setShowTnConnect] = useState(false);
  const [tnToken, setTnToken] = useState('');
  const [tnStoreId, setTnStoreId] = useState('');
  const [tnSaving, setTnSaving] = useState(false);
  const [waHistoryOrder, setWaHistoryOrder] = useState<Order | null>(null);
  const [waHistoryLogs, setWaHistoryLogs] = useState<any[]>([]);
  const [waHistoryLoading, setWaHistoryLoading] = useState(false);
  const [waGroups, setWaGroups] = useState<WaGroup[]>([]);
  const waIndex = useMemo(() => {
    const byOrderId = new Map<string, WaGroup[]>();
    const byTail = new Map<string, WaGroup[]>();
    for (const g of waGroups) {
      if (g.order_id) { if (!byOrderId.has(g.order_id)) byOrderId.set(g.order_id, []); byOrderId.get(g.order_id)!.push(g); }
      if (g.tail) { if (!byTail.has(g.tail)) byTail.set(g.tail, []); byTail.get(g.tail)!.push(g); }
    }
    return { byOrderId, byTail };
  }, [waGroups]);
  // Comprobantes / adjuntos entrantes de WhatsApp, indexados por cola de teléfono.
  const [incomingMedia, setIncomingMedia] = useState<Map<string, any[]>>(new Map());
  const [activityOrder, setActivityOrder] = useState<Order | null>(null);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Cargar el rol del usuario (profiles). Sin fila => 'admin' (no rompe logins existentes).
  // 2FA por mail (15/10/2026): la sesión tiene que estar verificada con el código (src/TwoFactor.tsx,
  // edge function two-factor). Se chequea una vez por sesión (claim session_id del JWT), no en cada
  // refresh del token. Si la consulta falla, se pide el código (fail-closed).
  const [twoFA, setTwoFA] = useState<'checking' | 'needed' | 'ok'>('checking');
  const [twoFAHint, setTwoFAHint] = useState('');
  const sessionId = useMemo(() => {
    try { return JSON.parse(atob(String(session?.access_token || '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).session_id || null; } catch { return null; }
  }, [session?.access_token]);
  useEffect(() => {
    if (!sessionId) { setTwoFA('checking'); return; }
    let alive = true;
    setTwoFA('checking');
    // Un error de red (típico al volver de segundo plano en el celular) NO es "sin verificar":
    // se reintenta antes de mostrar la pantalla del código (parecía que se había deslogueado).
    const check = (tries: number) => supabase.functions.invoke('two-factor', { body: { action: 'status' } }).then(({ data, error }) => {
      if (!alive) return;
      if (!data?.ok && error && tries > 0) { setTimeout(() => alive && check(tries - 1), 1500); return; }
      setTwoFAHint(data?.email_hint || '');
      setTwoFA(data?.ok && data?.verified ? 'ok' : 'needed');
    });
    check(3);
    return () => { alive = false; };
  }, [sessionId]);

  useEffect(() => {
    if (!session?.user) { setRole(null); return; }
    supabase
      .from('profiles')
      .select('role')
      .eq('user_id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        setRole(((data?.role as 'admin' | 'creator' | 'canje' | 'staff') || 'admin'));
      });
  }, [session]);

  // Rol staff: si entra por URL a una pantalla con plata (/costos, /productos…), va a Órdenes.
  useEffect(() => {
    if (role === 'staff' && STAFF_HIDDEN_TABS.includes(activeTab as string)) setActiveTab('orders' as any);
  }, [role, activeTab]);

  // Deep-links: cada pantalla tiene su ruta (/costos, /atencion, …). Al montar se abre la
  // tab de la URL (recargar ya no te tira a Pedidos), atrás/adelante del navegador navega
  // entre tabs, y al cambiar de tab se actualiza la URL para poder compartir el link.
  useEffect(() => {
    const applyPath = () => {
      const t = PATH_TABS[window.location.pathname.replace(/\/+$/, '') || '/'];
      if (t) setActiveTab(t as any);
    };
    applyPath();
    window.addEventListener('popstate', applyPath);
    return () => window.removeEventListener('popstate', applyPath);
  }, []);

  const didInitTabSync = useRef(false);
  useEffect(() => {
    // El primer render llega con la tab default ('orders'): si empujáramos la URL acá,
    // pisaríamos /costos con / antes de que applyPath aplique la ruta de la carga.
    if (!didInitTabSync.current) { didInitTabSync.current = true; return; }
    const path = TAB_PATHS[activeTab] || '/';
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
  }, [activeTab]);

  // Persist filters in sessionStorage
  useEffect(() => { sessionStorage.setItem('filterStatus', JSON.stringify(Array.from(filterStatus))); }, [filterStatus]);
  useEffect(() => { sessionStorage.setItem('filterShipping', JSON.stringify(Array.from(filterShipping))); }, [filterShipping]);
  useEffect(() => { sessionStorage.setItem('filterPaymentStatus', filterPaymentStatus); }, [filterPaymentStatus]);
  useEffect(() => { sessionStorage.setItem('filterDateRange', filterDateRange); }, [filterDateRange]);
  useEffect(() => { sessionStorage.setItem('filterSpecificDate', filterSpecificDate); }, [filterSpecificDate]);
  useEffect(() => { sessionStorage.setItem('sortDateOrder', sortDateOrder); }, [sortDateOrder]);
  useEffect(() => { sessionStorage.setItem('filterDeadline', filterDeadline); }, [filterDeadline]);
  useEffect(() => { sessionStorage.setItem('filterCanje', filterCanje); }, [filterCanje]);

  // Carga productos en stock (no requieren fabricación) — usado para badge de deadline.
  useEffect(() => {
    (async () => {
      const { data: prods } = await supabase.from('tn_products').select('id, name').eq('is_stock', true);
      if (!prods) return;
      setStockProductNames(prods.map((p: any) => (p.name || '').toLowerCase()));
      const ids = prods.map((p: any) => p.id);
      if (ids.length > 0) {
        const { data: vars } = await supabase.from('tn_variants').select('id').in('product_id', ids);
        if (vars) setStockVariantIds(new Set(vars.map((v: any) => v.id)));
      }
    })();
  }, []);

  // Helpers de fabricación + deadline. Política: 7-10 días corridos desde created_at.
  // Orden de fabricación = al menos 1 item NO está en stock.
  // El reloj corre solo en estados activos (nueva/cortado/falta_insumo). Una vez empaquetado en adelante, no aplica.
  const requiresFabrication = (order: Order): boolean => {
    const items = (order as any).products;
    if (!Array.isArray(items) || items.length === 0) return false;
    return items.some((item: any) => {
      if (item.tn_variant_id && stockVariantIds.has(item.tn_variant_id)) return false;
      const name = (item.name || '').toLowerCase();
      if (stockProductNames.length > 0 && stockProductNames.some(sn => sn && name.startsWith(sn))) return false;
      return true;
    });
  };
  // Plazo = la FECHA DE LISTO de la orden (ready_by, migr 098), ya no "7-10 días desde la
  // compra". Solo cuenta mientras no está empaquetada. Fechas en días corridos, hora AR.
  const todayAR = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
  const daysUntil = (iso: string) => Math.round((Date.parse(iso + 'T00:00:00Z') - Date.parse(todayAR() + 'T00:00:00Z')) / 86400000);
  const getDeadlineStatus = (order: Order): 'on_time' | 'due_soon' | 'late' | null => {
    if (!order.ready_by) return null;
    if (!['nueva', 'cortado', 'falta_insumo'].includes(order.status)) return null;
    const d = daysUntil(order.ready_by);
    if (d < 0) return 'late';
    if (d <= 2) return 'due_soon';
    return 'on_time';
  };
  const readyChip = (order: Order) => {
    if (!order.ready_by || ['presupuesto', 'cancelada', 'stock'].includes(order.status)) return null;
    const st = getDeadlineStatus(order);
    const d = daysUntil(order.ready_by);
    const fecha = order.ready_by.split('-').reverse().slice(0, 2).join('/');
    const when = st == null ? `Listo ${fecha}` : d < 0 ? `Atrasada ${-d}d` : d === 0 ? 'Listo HOY' : d === 1 ? 'Listo mañana' : `Listo ${fecha}`;
    const style = st === 'late' ? { background: '#dc2626', color: '#fff' }
      : st === 'due_soon' ? { background: '#f59e0b', color: '#fff' }
        : st === 'on_time' ? { background: 'rgba(22,163,74,0.14)', color: '#16a34a' }
          : { background: 'var(--bg-secondary)', color: 'var(--text-muted)' };
    const moved = order.ready_by_original && order.ready_by_original !== order.ready_by;
    return (
      <>
        <div className="source-badge" style={style}
          title={`Tiene que estar EMPAQUETADA el ${order.ready_by.split('-').reverse().join('/')}${moved ? ` (fecha original ${order.ready_by_original!.split('-').reverse().join('/')})` : ''}${order.ready_by_kind === 'promised' ? ' · comprometida con el cliente' : ' · calculada por los productos'}${order.delay_reason ? ' · demora: ' + order.delay_reason : ''}`}>
          📅 {when}
        </div>
        {order.ready_by_kind === 'promised' && (
          <div className="source-badge" title="Fecha comprometida con el cliente (o límite de MercadoLibre)" style={{ background: '#1c1917', color: '#fff' }}>🤝 Comprometida</div>
        )}
        {order.is_urgent && (
          <div className="source-badge" title="Marcada urgente: va primero en el taller" style={{ background: '#991b1b', color: '#fff' }}>🔥 Urgente</div>
        )}
      </>
    );
  };

  // Solo tema CLARO (10/10/2026): se eliminó el modo oscuro. Se fija el atributo y se borra
  // la preferencia vieja guardada, por si alguien lo tenía en oscuro.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    try { localStorage.removeItem('theme'); } catch { /* sin storage */ }
  }, []);

  const showSnackbar = useCallback((message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setSnackbars(prev => [...prev, { message, type, id }]);
    setTimeout(() => {
      setSnackbars(prev => prev.filter(s => s.id !== id));
    }, 3500);
  }, []);

  const dismissSnackbar = useCallback((id: number) => {
    setSnackbars(prev => prev.filter(s => s.id !== id));
  }, []);

  // Check MeLi & TN connection status
  useEffect(() => {
    async function checkConnections() {
      const [meliRes, tnRes] = await Promise.all([
        supabase.from('meli_tokens').select('nickname, last_sync_at').limit(1).maybeSingle(),
        supabase.from('tn_config').select('store_id').limit(1).maybeSingle(),
      ]);
      if (meliRes.data) {
        setMeliConnected(true);
        setMeliNickname(meliRes.data.nickname || 'MercadoLibre');
      }
      setTnConnected(!!tnRes.data);
      setConnChecked(true);
    }
    if (session) checkConnections();
  }, [session]);

  // Handle MeLi OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('meli_connected') === 'true') {
      showSnackbar('MercadoLibre conectado exitosamente', 'success');
      setMeliConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('meli_error')) {
      showSnackbar(`Error conectando MercadoLibre: ${params.get('meli_error')}`, 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('tn_connected') === 'true') {
      showSnackbar('Tiendanube conectado exitosamente', 'success');
      setTnConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('tn_error')) {
      showSnackbar(`Error conectando Tiendanube: ${params.get('tn_error')}`, 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchOrders();
    fetchNotesCount();
    fetchEscalatedCount();
    fetchTrashedOrders();
    fetchWaCounts();

    // Realtime SOLO para tablas de bajo volumen (orders/budgets/notes). Se sacó
    // whatsapp_logs a propósito: sus ráfagas (campañas de reviews/envíos) rate-limitaban
    // el canal de Realtime y tiraban también la suscripción de órdenes → las ventas no
    // "caían" solas. waCounts ahora se refresca por poll/focus (no es crítico).
    const channel = supabase
      .channel('app-changes')
      // Realtime dispara MUCHOS eventos seguidos (un sync de TN toca decenas de órdenes).
      // Debounce + refresco incremental: una ráfaga de cambios = una consulta chica.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => {
        scheduleIncrementalRefresh();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'budgets' }, () => {
        scheduleIncrementalRefresh();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notes' }, () => {
        fetchNotesCount();
      })
      .subscribe((status) => {
        // Al (re)conectar el websocket, resync por si se perdieron eventos.
        // Resync al (re)conectar el websocket. Incremental: si el WS se cae y reconecta varias
        // veces seguidas, no queremos una descarga completa por cada reconexión.
        if (status === 'SUBSCRIBED') { fetchOrders({ incremental: true }); fetchNotesCount(); }
      });

    // Defensa contra desync cuando la pestaña queda en background o el WS se cae:
    // (1) refetch inmediato al volver a la pestaña; (2) polling de respaldo cada 45s.
    // El poll de 45s solo refetcha órdenes/notas (livianas); waCounts es pesado y se
    // refresca por realtime (debounced) + al volver a la pestaña.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchOrders({ incremental: true });
        fetchNotesCount();
        fetchEscalatedCount();
        fetchWaCounts();
      }
    };
    const onFocus = () => { fetchOrders({ incremental: true }); fetchNotesCount(); fetchEscalatedCount(); fetchWaCounts(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    let pollTick = 0;
    const pollId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        pollTick++;
        // Cada 60s: solo lo que cambió (normalmente 0 filas). Cada 6h: resync completo,
        // por si se perdió algún evento o el reloj del cursor quedó desfasado. Con el trigger
        // de updated_at (migr 060) el incremental es confiable, así que el completo es red de
        // seguridad, no el mecanismo principal.
        fetchOrders(pollTick % 360 === 0 ? undefined : { incremental: true });
        fetchNotesCount();
        fetchEscalatedCount();
        if (pollTick % 15 === 0) fetchWaCounts(); // contador de WA cada ~15 min
      }
    }, 60000);

    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.clearInterval(pollId);
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'trash') fetchTrashedOrders();
  }, [activeTab]);

  // Trae el despiece de los combos MiPared de las órdenes visibles (1 fetch por code, cacheado).
  useEffect(() => {
    const codes = new Set<string>();
    for (const o of orders) getMiparedCodes(o).forEach(c => codes.add(c));
    codes.forEach(async (code) => {
      if (miparedFetchedRef.current.has(code)) return;
      miparedFetchedRef.current.add(code);
      setMiparedCombos(prev => ({ ...prev, [code]: { loading: true } }));
      try {
        const { data, error } = await supabase.functions.invoke('mipared-combo', { body: { code } });
        if (error || !data?.ok) throw new Error(data?.error || error?.message || 'error');
        setMiparedCombos(prev => ({ ...prev, [code]: { combo: data.combo, snapshot: data.snapshotDataUrl || null } }));
      } catch (e: any) {
        setMiparedCombos(prev => ({ ...prev, [code]: { error: String(e?.message || e) } }));
      }
    });
  }, [orders]);

  async function handleSync() {
    try {
      setLoading(true);
      showSnackbar('Sincronizando órdenes...', 'success');

      const promises: Promise<any>[] = [
        supabase.functions.invoke('sync-tn-orders'),
      ];

      if (meliConnected) {
        promises.push(supabase.functions.invoke('meli-sync-orders'));
      }

      const results = await Promise.allSettled(promises);

      const messages: string[] = [];
      const tnResult = results[0];
      if (tnResult.status === 'fulfilled' && !tnResult.value.error) {
        const d = tnResult.value.data;
        const newCount = d?.new || 0;
        messages.push(`TN: ${newCount} nueva${newCount !== 1 ? 's' : ''}`);
      } else {
        const err = tnResult.status === 'rejected' ? tnResult.reason : tnResult.value.error;
        console.error('TN sync error:', err);
        messages.push('TN: error');
      }

      if (meliConnected && results[1]) {
        const mlResult = results[1];
        if (mlResult.status === 'fulfilled' && !mlResult.value.error) {
          const d = mlResult.value.data;
          messages.push(`ML: ${d?.synced || 0} nuevas, ${d?.updated || 0} actualizadas`);
        } else {
          const err = mlResult.status === 'rejected' ? mlResult.reason : mlResult.value.error;
          console.error('ML sync error:', err);
          messages.push('ML: error');
        }
      }

      showSnackbar(`Sync: ${messages.join(' | ')}`, 'success');
      fetchOrders();
    } catch (err: any) {
      console.error('Sync error:', err);
      showSnackbar(`Error al sincronizar: ${err.message || 'Error desconocido'}`, 'error');
    } finally {
      setLoading(false);
    }
  }

  // Clave única para mezclar órdenes y presupuestos en una sola lista (los ids se pisan entre tablas).
  const rowKey = (r: any) => `${r.status === 'presupuesto' ? 'b' : 'o'}${r.id}`;

  // Agrupa las ráfagas de realtime en un solo refresco incremental.
  function scheduleIncrementalRefresh() {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      fetchOrders({ incremental: true });
    }, 2000);
  }

  // fetchOrders tiene DOS modos. El completo baja las ~4400 órdenes (varios MB) y solo se usa
  // al montar y cada 30 min. El incremental trae únicamente lo que cambió desde el último sync
  // (casi siempre 0 filas) y es el que corre en el polling y en cada evento de realtime.
  //
  // ⚠️ El modo completo corriendo cada 20s + en cada cambio de realtime fue lo que reventó la
  // cuota de egress de Supabase el 2026-08-07 (el proyecto quedó bloqueado con 402 y no se podía
  // ni loguear). No volver a llamarlo en un intervalo corto.
  async function fetchOrders(opts?: { incremental?: boolean }) {
    const since = lastOrdersSyncRef.current;
    // 29/10/2026: un refresco INCREMENTAL antes de que termine la primera carga (realtime
    // SUBSCRIBED / focus / visibilidad al abrir el admin) no tenía cursor y se convertía en OTRA
    // carga completa → el historial se bajaba 2+ veces por apertura (~21 MB). Ahora se ignora:
    // la carga en curso trae todo. Y nunca dos cargas completas a la vez.
    if (opts?.incremental && !since) return;
    if (!opts?.incremental && fullLoadInFlightRef.current) return;
    const incremental = !!opts?.incremental && !!since;
    if (!incremental) fullLoadInFlightRef.current = true;
    try {
      if (incremental) {
        const [oRes, bRes] = await Promise.all([
          supabase.from('orders').select('*').gt('updated_at', since!).order('updated_at', { ascending: true }).limit(1000),
          supabase.from('budgets').select('*').gt('updated_at', since!).order('updated_at', { ascending: true }).limit(1000),
        ]);
        if (oRes.error) throw oRes.error;
        if (bRes.error) throw bRes.error;
        const changed = [
          ...(oRes.data || []),
          ...(bRes.data || []).map((b: any) => ({ ...b, status: 'presupuesto' })),
        ];
        if (!changed.length) return;
        for (const r of changed) {
          if (r.updated_at && (!lastOrdersSyncRef.current || r.updated_at > lastOrdersSyncRef.current)) {
            lastOrdersSyncRef.current = r.updated_at;
          }
        }
        setOrders(prev => {
          const map = new Map(prev.map((o: any) => [rowKey(o), o]));
          for (const r of changed) {
            // Una orden que volvió con deleted_at se fue a la papelera → sale de la lista.
            if (r.deleted_at) map.delete(rowKey(r));
            else map.set(rowKey(r), r);
          }
          return Array.from(map.values())
            .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        });
        return;
      }

      setOrdersLoading(true);
      // PostgREST corta en 1000 filas/request: se pagina, y las páginas van EN PARALELO.
      const pageSize = 1000;
      const HIST = '("entregado","cancelada","stock")';
      // `filters` se aplica DESPUÉS del select: el count/head solo funciona en el primer select().
      const fetchAll = async (filters: (q: any) => any) => {
        const head = await filters(supabase.from('orders').select('id', { count: 'exact', head: true }));
        if (head.error) throw head.error;
        const total = head.count || 0;
        const pages = await Promise.all(Array.from({ length: Math.ceil(total / pageSize) }, (_, i) =>
          filters(supabase.from('orders').select('*')).order('created_at', { ascending: false }).range(i * pageSize, i * pageSize + pageSize - 1)));
        const bad = pages.find((r: any) => r.error);
        if (bad) throw bad.error;
        return pages.flatMap((r: any) => r.data || []);
      };
      const merge = (rows: any[]) => setOrders(prev => {
        // Las que ya están (pueden venir más nuevas por un refresco incremental) no se pisan.
        const map = new Map(rows.map((o: any) => [rowKey(o), o]));
        for (const o of prev) map.set(rowKey(o), o);
        return Array.from(map.values())
          .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      });
      const bumpCursor = (rows: any[]) => {
        for (const r of rows) {
          if (r.updated_at && (!lastOrdersSyncRef.current || r.updated_at > lastOrdersSyncRef.current)) lastOrdersSyncRef.current = r.updated_at;
        }
      };

      // 1) Abiertas + presupuestos → la grilla aparece enseguida
      const [openRows, budgetsRes] = await Promise.all([
        fetchAll(q => q.is('deleted_at', null).not('status', 'in', HIST)),
        supabase.from('budgets').select('*').order('created_at', { ascending: false }),
      ]);
      if (budgetsRes.error) throw budgetsRes.error;
      const first = [...openRows, ...(budgetsRes.data || []).map((b: any) => ({ ...b, status: 'presupuesto' }))];
      // reemplazo completo: en una recarga total no deben quedar filas viejas
      setOrders(first.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
      lastOrdersSyncRef.current = null;
      bumpCursor(first);
      setOrdersLoading(false);

      // 2) Historial en segundo plano (stats, búsquedas, "ver entregadas", BI).
      // Con caché en el navegador (src/ordersCache.ts, 29/10/2026): se usa la copia local y del
      // servidor solo baja lo que cambió desde la última vez (antes: ~5.200 filas / ~4 MB por apertura).
      const HIST_ST = ['entregado', 'cancelada', 'stock'];
      const uid = (await supabase.auth.getSession()).data.session?.user?.id || ''; // el estado `session` puede no estar todavía
      const cached = uid ? await readOrdersHistory(uid) : null;
      let histRows: any[];
      let deltaMax: string | null = null;
      if (cached) {
        const byId = new Map<any, any>(cached.rows.map((o: any) => [o.id, o]));
        // TODO lo que cambió desde la copia (cualquier estado, incluidas las que fueron a la papelera)
        const delta = cached.maxUpdated ? await fetchAll(q => q.gt('updated_at', cached.maxUpdated)) : [];
        deltaMax = delta.reduce((m: string | null, r: any) => (r.updated_at && (!m || r.updated_at > m) ? r.updated_at : m), null);
        for (const r of delta) {
          if (r.deleted_at || !HIST_ST.includes(r.status)) byId.delete(r.id); // salió del historial
          else byId.set(r.id, r);
        }
        histRows = Array.from(byId.values());
      } else {
        histRows = await fetchAll(q => q.is('deleted_at', null).in('status', HIST_ST));
      }
      merge(histRows);
      bumpCursor(histRows);
      setHistoryLoaded(true);
      if (uid) {
        const maxUpdated = histRows.reduce((m: string | null, r: any) => (r.updated_at && (!m || r.updated_at > m) ? r.updated_at : m),
          [cached?.maxUpdated || null, deltaMax].filter(Boolean).sort().pop() || null);
        // la fecha de la copia se mantiene (vence a los 3 días desde la última carga COMPLETA)
        writeOrdersHistory({ userId: uid, savedAt: cached ? cached.savedAt : Date.now(), maxUpdated, rows: histRows });
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      if (!incremental) { setOrdersLoading(false); fullLoadInFlightRef.current = false; }
    }
  }

  // Lee la vista AGRUPADA (2278 filas / 99 kB) en vez de las ~5000 filas crudas (421 kB).
  // Cada log cae en exactamente un par (order_id, tail), así que sumar los grupos que
  // matchean da el mismo número que el Set de ids que se usaba antes.
  async function fetchWaCounts() {
    try {
      const { data, error } = await supabase.from('wa_counts_grouped').select('order_id, tail, n').limit(20000);
      if (error) throw error;
      setWaGroups((data || []) as WaGroup[]);
    } catch (err) {
      console.error('Error fetching WA counts:', err);
    }
    fetchIncomingMedia();
  }

  // Trae los mensajes ENTRANTES con adjunto que son COMPROBANTES (imagen o PDF),
  // indexados por cola de teléfono, para mostrarlos en la card de la orden que
  // corresponda. Stickers, notas de voz y videos quedan afuera: se ven en el chat
  // del Inbox, no son un adjunto de la orden (ver src/receipts.ts).
  async function fetchIncomingMedia() {
    try {
      const byPhone = new Map<string, any[]>();
      const pageSize = 1000;
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from('whatsapp_logs')
          .select('id, phone, media_url, media_mime_type, message_text, created_at')
          .eq('direction', 'incoming')
          .not('media_url', 'is', null)
          .or(RECEIPT_MIME_OR).or(RECEIPT_KIND_OR)
          .neq('media_mime_type', STICKER_MIME)
          .order('id', { ascending: true })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const rows = data || [];
        for (const r of rows) {
          if (!isReceiptMime(r.media_mime_type)) continue;
          const key = phoneTail(r.phone);
          if (!key) continue;
          if (!byPhone.has(key)) byPhone.set(key, []);
          byPhone.get(key)!.push(r);
        }
        if (rows.length < pageSize) break;
        from += pageSize;
      }
      setIncomingMedia(byPhone);
    } catch (err) {
      console.error('Error fetching incoming media:', err);
    }
  }

  // Comprobantes recibidos para una orden: adjuntos entrantes del mismo teléfono,
  // recibidos desde ~1h antes de la creación de la orden en adelante.
  function getReceiptsForOrder(order: Order): any[] {
    if (!order.contact_phone) return [];
    const all = incomingMedia.get(phoneTail(order.contact_phone)) || [];
    const t0 = new Date(order.created_at).getTime() - 3600000;
    // Lo que llega DESPUÉS de entregado no es un comprobante de esta orden (fotos para la review,
    // consultas). Fotos de reviews y lo marcado "No es comprobante" ya vienen filtrados por
    // media_kind (RECEIPT_KIND_OR, migr 105).
    const t1 = (order as any).delivered_at ? new Date((order as any).delivered_at).getTime() : Infinity;
    return all
      .filter(m => { const t = new Date(m.created_at).getTime(); return t >= t0 && t <= t1; })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  function getWaCount(order: Order): number {
    const orderId = getOrderDisplayId(order);
    const tail = (order.contact_phone || '').replace(/\D/g, '').slice(-10);
    // Un grupo puede matchear por orden Y por teléfono a la vez: lo contamos una sola vez.
    const seen = new Set<WaGroup>();
    let total = 0;
    for (const g of waIndex.byOrderId.get(orderId) || []) { if (!seen.has(g)) { seen.add(g); total += g.n; } }
    if (tail) for (const g of waIndex.byTail.get(tail) || []) { if (!seen.has(g)) { seen.add(g); total += g.n; } }
    return total;
  }

  async function fetchNotesCount() {
    try {
      const { count, error } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('is_done', false);

      if (error) throw error;
      setNotesCount(count || 0);
    } catch (err) {
      console.error('Error fetching notes count:', err);
    }
  }

  // Cuenta las conversaciones escaladas a un humano y todavía SIN atender, para el badge de
  // la pestaña "Atención". Una escalada está "abierta" si no hay respuesta humana manual
  // (outgoing, is_agent=false) posterior — misma lógica que la bandeja. Se auto-limpia al responder.
  async function fetchEscalatedCount() {
    try {
      const since = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
      const [escRes, humanRes, disRes] = await Promise.all([
        supabase.from('whatsapp_logs').select('phone, created_at').eq('is_escalation', true).gt('created_at', since),
        supabase.from('whatsapp_logs').select('phone, created_at').eq('direction', 'outgoing').eq('is_agent', false).gt('created_at', since),
        supabase.from('inbox_dismissals').select('tail, dismissed_at'),
      ]);
      const tailOf = (p: string) => (p || '').replace(/\D/g, '').slice(-10);
      const lastHuman = new Map<string, number>();
      for (const r of humanRes.data || []) {
        const t = tailOf(r.phone); if (!t) continue;
        const ts = new Date(r.created_at).getTime();
        if (ts > (lastHuman.get(t) || 0)) lastHuman.set(t, ts);
      }
      const lastEsc = new Map<string, number>();
      for (const r of escRes.data || []) {
        const t = tailOf(r.phone); if (!t) continue;
        const ts = new Date(r.created_at).getTime();
        if (ts > (lastEsc.get(t) || 0)) lastEsc.set(t, ts);
      }
      // "Resuelta" a mano en la bandeja también apaga la campanita (hasta que vuelva a escalar).
      const dismissed = new Map<string, number>((disRes.data || []).map((d: any) => [d.tail, new Date(d.dismissed_at).getTime()]));
      let n = 0;
      lastEsc.forEach((ts, t) => { if (ts > (lastHuman.get(t) || 0) && ts > (dismissed.get(t) || 0)) n++; });
      setEscalatedCount(n);
    } catch (err) {
      console.error('Error fetching escalated count:', err);
    }
  }

  function getTableForOrder(order: Order): 'orders' | 'budgets' {
    return order.status === 'presupuesto' ? 'budgets' : 'orders';
  }

  async function toggleProductCut(orderId: number, productIndex: number) {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const newProducts = [...(order.products || [])];
    newProducts[productIndex] = {
      ...newProducts[productIndex],
      is_cut: !newProducts[productIndex].is_cut
    };

    const allItemsCut = newProducts.length > 0 && newProducts.every((p: any) => p.is_cut);
    const autoUpdateStatus = allItemsCut && order.status === 'nueva';
    const finalStatus = autoUpdateStatus ? 'cortado' : order.status;

    // Optimistic update
    setOrders(prev =>
      prev.map(o => o.id === orderId ? { ...o, products: newProducts, status: finalStatus } : o)
    );

    try {
      const updatePayload: any = { products: newProducts };
      if (autoUpdateStatus) {
        updatePayload.status = 'cortado';
      }

      const table = getTableForOrder(order);
      const { error } = await supabase
        .from(table)
        .update(updatePayload)
        .eq('id', orderId);

      if (error) throw error;

      if (autoUpdateStatus) {
        showSnackbar(`¡Orden #${getOrderDisplayId(order)} marcada como CORTADO automáticamente!`, 'success');
        logActivity(session?.user.email, 'ORDEN_CORTADA_AUTO', { order_id: order.id, tiendanube_id: order.tiendanube_id });
      } else {
        logActivity(session?.user.email, 'PRODUCTO_CORTADO', { order_id: order.id, product_index: productIndex, product_name: newProducts[productIndex].name, is_cut: newProducts[productIndex].is_cut });
      }
    } catch (err) {
      console.error('Error updating product status:', err);
      fetchOrders();
      showSnackbar('Error al actualizar el producto', 'error');
    }
  }

  // Marca/desmarca un item como RETIRADO (se tacha). Independiente de is_cut (cortado).
  async function toggleProductPicked(orderId: number, productIndex: number) {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const newProducts = [...(order.products || [])];
    newProducts[productIndex] = {
      ...newProducts[productIndex],
      is_picked: !newProducts[productIndex].is_picked,
    };

    // Optimistic update
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, products: newProducts } : o));

    try {
      const table = getTableForOrder(order);
      const { error } = await supabase.from(table).update({ products: newProducts }).eq('id', orderId);
      if (error) throw error;
      logActivity(session?.user.email, 'PRODUCTO_RETIRADO', {
        order_id: order.id,
        product_index: productIndex,
        product_name: newProducts[productIndex].name,
        is_picked: newProducts[productIndex].is_picked,
      });
    } catch (err) {
      console.error('Error updating picked status:', err);
      fetchOrders();
      showSnackbar('Error al actualizar el producto', 'error');
    }
  }

  async function updateOrderStatus(id: number, newStatus: string) {
    const order = orders.find(o => o.id === id);
    if (!order) return;

    if (order.status === newStatus) return;

    // Si es una orden de Tiendanube que se está cancelando, ofrecer cancelarla también en TN.
    if (newStatus === 'cancelada' && order.source === 'tiendanube' && order.status !== 'cancelada' && order.tiendanube_id) {
      const alsoTn = window.confirm(`La orden #${getOrderDisplayId(order)} es de Tiendanube.\n\n¿Cancelarla también en Tiendanube?\n(Devuelve el stock de los productos y le avisa al cliente por mail.)`);
      if (alsoTn) {
        try {
          const { data, error } = await supabase.functions.invoke('tn-cancel-order', { body: { number: String(order.tiendanube_id) } });
          if (error) throw error;
          if (data?.ok) {
            showSnackbar(data.already_cancelled ? 'Ya estaba cancelada en Tiendanube' : '✓ Cancelada también en Tiendanube', 'success');
            logActivity(session?.user.email, 'CANCELADA_EN_TN', { order_id: id, tiendanube_id: order.tiendanube_id });
          } else {
            showSnackbar(`No se pudo cancelar en Tiendanube: ${data?.error || 'error'}`, 'error');
          }
        } catch (e: any) {
          showSnackbar(`Error cancelando en Tiendanube: ${e.message || e}`, 'error');
        }
      }
    }

    // "Falta insumo" = bloqueada en el taller: se pide QUÉ falta, así el taller y quien
    // compra lo ven (sin motivo era un estado mudo). Cancelar el prompt cancela el cambio.
    let delayReason: string | null = null;
    if (newStatus === 'falta_insumo') {
      const r = window.prompt(`¿Qué insumo falta para #${getOrderDisplayId(order)}?`, order.delay_reason || '');
      if (r === null) return;
      delayReason = r.trim() || null;
    }

    // Move logic
    const oldTable = getTableForOrder(order);
    const newTable = newStatus === 'presupuesto' ? 'budgets' : 'orders';

    if (oldTable !== newTable) {
      // We need to move the record
      try {
        // Optimistic update (might be risky here, but user exp first)
        setOrders(prev =>
          prev.map(o =>
            o.id === id ? { ...o, status: newStatus, payment_status: newStatus === 'entregado' ? 'paid' : o.payment_status } : o
          )
        );

        // 1. Insert into new table
        const { id: _, tiendanube_id, ...orderData } = order as any;
        const payload = {
          ...orderData,
          status: newStatus,
          updated_at: new Date().toISOString()
        };

        if (newTable === 'orders') {
          // Moving to orders
          await supabase.from('orders').insert({ ...payload, tiendanube_id: null });
        } else {
          // Moving to budgets
          await supabase.from('budgets').insert(payload);
        }

        // 2. Delete from old table
        await supabase.from(oldTable).delete().eq('id', id);

        showSnackbar(`Estado actualizado a "${STATUS_LABELS[newStatus]}" (Movido a ${newTable})`, 'success');
        logActivity(session?.user.email, 'CAMBIO_ESTADO_MOVIDO', { order_id: id, old_status: order.status, new_status: newStatus, old_table: oldTable, new_table: newTable });
        // Check fetching to get new ID
        fetchOrders();
      } catch (err: any) {
        console.error('Error moving order:', err);
        fetchOrders(); // Revert
        showSnackbar(`Error al mover la orden: ${err.message}`, 'error');
      }
      return;
    }

    // Normal update (same table)
    setOrders(prev =>
      prev.map(order =>
        order.id === id ? { ...order, status: newStatus, payment_status: newStatus === 'entregado' ? 'paid' : order.payment_status, ...(newStatus === 'falta_insumo' ? { delay_reason: delayReason } : {}) } : order
      )
    );

    try {
      const updatePayload: any = { status: newStatus };
      if (newStatus === 'falta_insumo') updatePayload.delay_reason = delayReason;
      if (newStatus === 'entregado') {
        updatePayload.payment_status = 'paid';
      }

      const { error } = await supabase
        .from(oldTable)
        .update(updatePayload)
        .eq('id', id);

      if (error) throw error;
      showSnackbar(`Estado actualizado a "${STATUS_LABELS[newStatus]}"`, 'success');
      logActivity(session?.user.email, 'CAMBIO_ESTADO', { order_id: id, old_status: order.status, new_status: newStatus });

      // Guard: una orden de TRANSFERENCIA A CONVENIR todavía IMPAGA no debe recibir "listo para
      // retirar" — primero tiene que pagar. (El efectivo SÍ puede: paga al retirar.) Sin esto, se le
      // avisó a Milena que retire mientras le llegaban los recordatorios de transferencia.
      const transferPendingImpaga = isTransferenciaConvenir(order) && order.payment_status !== 'paid';
      if (transferPendingImpaga && newStatus === 'empaquetado' && isStorePickup(order)) {
        showSnackbar('⚠️ Orden por transferencia sin pagar: NO se envió "listo para retirar" (esperá el pago).', 'error');
      }

      // "Listo para retirar" (22/10/2026): lo manda SOLO la cola process-pickup-notifications
      // (trigger orders_pickup_notification al pasar a empaquetado, ~1,5 min de espera para poder
      // deshacer). Antes además se mandaba directo desde acá → cuando el guard de la cola dejó de
      // ver el envío (status 'sent' → 'delivered'), salió DOS veces (10 pedidos del 19 al 22/10).
      if (newStatus === 'empaquetado' && order.contact_phone && isStorePickup(order) && order.source !== 'mercadolibre' && !transferPendingImpaga) {
        showSnackbar('📦 En ~2 min se le avisa por WhatsApp que lo puede retirar', 'success');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      // Revert on error
      fetchOrders();
      showSnackbar('Error al actualizar el estado', 'error');
    }
  }

  // Descarga un archivo remoto (Storage está en otro dominio, así que el atributo
  // download de <a> no alcanza: hay que bajarlo como blob).
  async function downloadFile(url: string, filename: string) {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const ext = (() => {
        const m = url.split('?')[0].match(/\.([a-zA-Z0-9]+)$/);
        if (m) return m[1].toLowerCase();
        return blob.type.includes('pdf') ? 'pdf' : 'jpg';
      })();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${filename}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch {
      window.open(url, '_blank'); // fallback: al menos abrirlo
    }
  }

  // "No es comprobante": un adjunto de WhatsApp que no es un pago (foto del producto, de la
  // review, una consulta). Se reclasifica (media_kind='other') y deja de verse en la card y en
  // Transferencias; sigue en el chat.
  async function markNotReceipt(mediaId: string | number) {
    const { error } = await supabase.from('whatsapp_logs').update({ media_kind: 'other' }).eq('id', mediaId);
    if (error) { alert('No se pudo reclasificar: ' + error.message); return; }
    setIncomingMedia(prev => {
      const next = new Map(prev);
      next.forEach((arr, k) => next.set(k, arr.filter(m => m.id !== mediaId)));
      return next;
    });
  }
  // Quitar un comprobante adjuntado a mano (se equivocaron de archivo o de orden)
  async function removeManualReceipt(order: Order, url: string) {
    if (!window.confirm('¿Quitar este comprobante de la orden?')) return;
    const urls = (((order as any).receipt_urls || []) as string[]).filter(u => u !== url);
    const { error } = await supabase.from('orders').update({ receipt_urls: urls }).eq('id', order.id);
    if (error) { alert('No se pudo quitar: ' + error.message); return; }
    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, receipt_urls: urls } as any : o));
  }

  // Adjuntar comprobante a mano (showroom/offline que no pasa por WhatsApp): sube al
  // bucket público whatsapp-media (donde ya viven los comprobantes) y lo guarda en
  // orders.receipt_urls. La conciliación de Transferencias lo toma igual que uno de WA.
  async function attachReceipt(order: Order, file: File) {
    try {
      const ext = ((file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg');
      const path = `manual_${order.id}_${Date.now()}.${ext}`;
      const url = await uploadFile('whatsapp-media', path, file, file.type || 'image/jpeg'); // R2 (02/11/2026)
      const urls = [...((order as any).receipt_urls || []), url];
      const { error } = await supabase.from('orders').update({ receipt_urls: urls }).eq('id', order.id);
      if (error) throw error;
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, receipt_urls: urls } as any : o));
    } catch (e: any) {
      alert('Error subiendo el comprobante: ' + (e?.message || e));
    }
  }

  async function updateOrderPaymentStatus(id: number, status: 'paid' | 'pending' | 'pending_cash') {
    const order = orders.find(o => o.id === id);
    if (!order) return;

    const actualStatus = status === 'pending_cash' ? 'pending' : status;
    const isCash = status === 'pending_cash';

    // Optimistic update
    setOrders(prev =>
      prev.map(o =>
        o.id === id ? {
          ...o,
          payment_status: actualStatus,
          payment_details: { ...o.payment_details, method: isCash ? 'custom' : (o.payment_details?.method === 'custom' ? 'other' : o.payment_details?.method) }
        } : o
      )
    );

    try {
      const table = getTableForOrder(order);
      const { error } = await supabase
        .from(table)
        .update({
          payment_status: actualStatus,
          payment_details: { ...order.payment_details, method: isCash ? 'custom' : (order.payment_details?.method === 'custom' ? 'other' : order.payment_details?.method) }
        })
        .eq('id', id);

      if (error) throw error;
      showSnackbar(`Orden #${id} marcada como ${status === 'paid' ? 'pagada' : status === 'pending_cash' ? 'pendiente (efectivo)' : 'pendiente'}`, 'success');
      logActivity(session?.user.email, 'CAMBIO_PAGO', { order_id: id, new_payment_status: status });

      // Send WhatsApp confirmation: when marked as paid (not cash), or when marked as pending_cash if no WA was sent before
      const shouldSendWa = order.contact_phone && order.source !== 'mercadolibre' && (
        (status === 'paid' && order.payment_status !== 'paid' && !isCash) ||
        (isCash)
      );

      if (shouldSendWa) {
        const orderNumber = getOrderDisplayId(order);
        try {
          // For pending_cash: only send if no WA was sent to this order before
          if (isCash) {
            const { data: existingLogs } = await supabase
              .from('whatsapp_logs')
              .select('id')
              .eq('order_id', orderNumber)
              .eq('direction', 'outgoing')
              // 'sent' pasa a 'delivered'/'read' con los avisos de Meta (migr 109): todos cuentan como enviado
              .in('status', ['sent', 'delivered', 'read'])
              .limit(1);
            if (existingLogs && existingLogs.length > 0) {
              // Already sent WA to this order, skip
            } else {
              const firstName = (order.contact_name || '').split(' ')[0] || 'Cliente';
              const templateName = await resolveTemplateName('order_confirmation_util');
              const { data: wpResult, error: wpError } = await supabase.functions.invoke('whatsapp-proxy', {
                body: {
                  phone: order.contact_phone,
                  templateName,
                  language: 'es_AR',
                  variables: [
                    { value: firstName },
                    { value: orderNumber }
                  ]
                }
              });

              if (wpError || !wpResult?.success) {
                const errorMsg = wpError?.message || wpResult?.error || 'Unknown error';
                const finalError = wpResult?.details ? `${errorMsg} | Meta JSON: ${JSON.stringify(wpResult.details)}` : errorMsg;
                await supabase.from('whatsapp_logs').insert({
                  direction: 'outgoing', phone: order.contact_phone, contact_name: order.contact_name,
                  template_name: templateName, status: 'failed', error_message: finalError, order_id: orderNumber
                });
              } else {
                await supabase.from('whatsapp_logs').insert({
                  direction: 'outgoing', phone: order.contact_phone, contact_name: order.contact_name,
                  template_name: templateName, status: 'sent', order_id: orderNumber,
                  meta_message_id: wpResult?.data?.messages?.[0]?.id
                });
                showSnackbar('WhatsApp de confirmación enviado (efectivo)', 'success');
              }
            }
          } else if (await (async () => {
            // Pagada (no efectivo): NO repetir si ya se le mandó la confirmación (22/10/2026: volver a
            // marcar pagada una orden — pendiente→pagada — le mandaba otra; K-25766, K-25768).
            const { data: prev } = await supabase.from('whatsapp_logs').select('id')
              .eq('order_id', orderNumber).eq('direction', 'outgoing')
              .in('template_name', ['order_confirmation_util', 'order_confirmation'])
              .in('status', ['sent', 'delivered', 'read']).limit(1);
            return !!prev?.length;
          })()) {
            showSnackbar('Ya se le había mandado la confirmación por WhatsApp: no se repite', 'success');
          } else {
            // Paid (not cash) - primera confirmación
            const firstName = (order.contact_name || '').split(' ')[0] || 'Cliente';
            const templateName = await resolveTemplateName('order_confirmation_util');
            const { data: wpResult, error: wpError } = await supabase.functions.invoke('whatsapp-proxy', {
              body: {
                phone: order.contact_phone,
                templateName,
                language: 'es_AR',
                variables: [
                  { value: firstName },
                  { value: orderNumber }
                ]
              }
            });

            if (wpError || !wpResult?.success) {
              const errorMsg = wpError?.message || wpResult?.error || 'Unknown error';
              const finalError = wpResult?.details ? `${errorMsg} | Meta JSON: ${JSON.stringify(wpResult.details)}` : errorMsg;
              await supabase.from('whatsapp_logs').insert({
                direction: 'outgoing', phone: order.contact_phone, contact_name: order.contact_name,
                template_name: templateName, status: 'failed', error_message: finalError, order_id: orderNumber
              });
            } else {
              await supabase.from('whatsapp_logs').insert({
                direction: 'outgoing', phone: order.contact_phone, contact_name: order.contact_name,
                template_name: templateName, status: 'sent', order_id: orderNumber,
                meta_message_id: wpResult?.data?.messages?.[0]?.id
              });
            }
          }
        } catch (e: any) {
          console.error('Error sending payment confirmation WA:', e);
          // No dejar fallar silencioso: dejar rastro en whatsapp_logs.
          try {
            await supabase.from('whatsapp_logs').insert({
              direction: 'outgoing', phone: order.contact_phone, contact_name: order.contact_name,
              template_name: 'order_confirmation_util', status: 'failed',
              error_message: `Excepción JS: ${e?.message || String(e)}`,
              order_id: getOrderDisplayId(order),
            });
          } catch (logErr) {
            console.error('No pude loguear el fallo de WA:', logErr);
          }
        }
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      fetchOrders();
      showSnackbar('Error al actualizar el pago', 'error');
    }
  }

  async function updateOrderInternalNote(id: number, note: string) {
    const order = orders.find(o => o.id === id);
    if (!order) return;

    // Optimistic update
    setOrders(prev =>
      prev.map(order =>
        order.id === id ? { ...order, owner_note: note } : order
      )
    );

    try {
      const table = getTableForOrder(order);
      const { error } = await supabase
        .from(table)
        .update({ owner_note: note })
        .eq('id', id);

      if (error) throw error;
      showSnackbar('Nota interna guardada', 'success');
      setEditingNoteId(null);
      logActivity(session?.user.email, 'NOTA_INTERNA', { order_id: id, note: note });
    } catch (err) {
      console.error('Error updating note:', err);
      fetchOrders();
      showSnackbar('Error al guardar la nota', 'error');
    }
  }

  async function saveTnConfig() {
    if (!tnToken.trim() || !tnStoreId.trim()) {
      showSnackbar('Completá el token y el Store ID', 'error');
      return;
    }
    setTnSaving(true);
    try {
      // Upsert: delete old and insert new
      await supabase.from('tn_config').delete().neq('id', 0);
      const { error } = await supabase.from('tn_config').insert({
        store_id: tnStoreId.trim(),
        access_token: tnToken.trim(),
        app_id: '26338',
      });
      if (error) throw error;
      setTnConnected(true);
      setShowTnConnect(false);
      setTnToken('');
      setTnStoreId('');
      showSnackbar('Tiendanube conectado exitosamente', 'success');
    } catch (err: any) {
      showSnackbar(`Error: ${err.message}`, 'error');
    } finally {
      setTnSaving(false);
    }
  }

  function openWhatsAppChat(phone: string | undefined) {
    if (!phone) return;
    let digits = phone.replace(/\D/g, '');
    // Argentina: si empieza con "54" pero no tiene el "9" obligatorio para móviles, lo agregamos.
    // Casos: 54 11 1234 5678 → 5491112345678
    if (digits.startsWith('54') && !digits.startsWith('549') && digits.length >= 12) {
      digits = '549' + digits.slice(2);
    }
    if (!digits) return;
    window.open(`https://wa.me/${digits}`, '_blank', 'noopener,noreferrer');
  }

  async function openWaHistory(order: Order) {
    setWaHistoryOrder(order);
    setWaHistoryLoading(true);
    try {
      const orderNumber = getOrderDisplayId(order);
      const orderCreatedAt = order.created_at ? new Date(order.created_at).getTime() : 0;

      // Search by order_id or phone
      const queries = [
        supabase.from('whatsapp_logs').select('*').eq('order_id', orderNumber).order('created_at', { ascending: true }),
      ];
      if (order.contact_phone) {
        queries.push(
          supabase.from('whatsapp_logs').select('*').eq('phone', order.contact_phone).order('created_at', { ascending: true })
        );
      }

      const results = await Promise.all(queries);
      const allLogs = new Map<string, any>();
      for (const res of results) {
        for (const log of (res.data || [])) {
          allLogs.set(log.id, log);
        }
      }

      // Filtrar logs taggeados a OTRA orden (estos pertenecen a otro pedido del mismo cliente).
      // Conservar los sin order_id solo si son posteriores a la creación de ESTA orden — son
      // típicamente respuestas/auto-replies recientes del cliente.
      const filtered = Array.from(allLogs.values()).filter((log: any) => {
        if (log.order_id === orderNumber) return true;
        if (!log.order_id) {
          return orderCreatedAt === 0 || new Date(log.created_at).getTime() >= orderCreatedAt;
        }
        return false;
      });

      const sorted = filtered.sort(
        (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      setWaHistoryLogs(sorted);
    } catch (err) {
      console.error('Error fetching WA history:', err);
    } finally {
      setWaHistoryLoading(false);
    }
  }

  async function openOrderActivity(order: Order) {
    setActivityOrder(order);
    setActivityLoading(true);
    try {
      const orQuery = order.tiendanube_id
        ? `details->>order_id.eq.${order.id},details->>id.eq.${order.id},details->>tiendanube_id.eq.${order.tiendanube_id}`
        : `details->>order_id.eq.${order.id},details->>id.eq.${order.id}`;
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .or(orQuery)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      setActivityLogs(data || []);
    } catch (err) {
      console.error('Error fetching activity for order:', err);
      setActivityLogs([]);
    } finally {
      setActivityLoading(false);
    }
  }

  async function deleteOrder(order: Order) {
    const displayId = getOrderDisplayId(order);
    const table = getTableForOrder(order);
    const isBudget = table === 'budgets';
    const confirmMsg = isBudget
      ? `¿Eliminar el presupuesto #${displayId} de ${order.contact_name}? Esta acción no se puede deshacer.`
      : `¿Mover la orden #${displayId} de ${order.contact_name} a la papelera? Vas a poder restaurarla desde ahí.`;

    if (!window.confirm(confirmMsg)) return;

    try {
      if (isBudget) {
        const { error } = await supabase.from('budgets').delete().eq('id', order.id);
        if (error) throw error;
        setOrders(prev => prev.filter(o => o.id !== order.id));
        showSnackbar('Presupuesto eliminado correctamente', 'success');
      } else {
        const { error } = await supabase
          .from('orders')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', order.id);
        if (error) throw error;
        setOrders(prev => prev.filter(o => o.id !== order.id));
        showSnackbar('Orden movida a la papelera', 'success');
      }
      logActivity(session?.user.email, 'ELIMINAR_ORDEN', { order_id: order.id, tiendanube_id: order.tiendanube_id, contact_name: order.contact_name, soft: !isBudget });
    } catch (err) {
      console.error('Error deleting order:', err);
      showSnackbar('Error al eliminar la orden', 'error');
    }
  }

  async function fetchTrashedOrders() {
    try {
      setTrashLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });
      if (error) throw error;
      setTrashedOrders(data || []);
    } catch (err) {
      console.error('Error fetching trash:', err);
      showSnackbar('Error al cargar la papelera', 'error');
    } finally {
      setTrashLoading(false);
    }
  }

  async function restoreOrder(order: Order) {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ deleted_at: null })
        .eq('id', order.id);
      if (error) throw error;
      setTrashedOrders(prev => prev.filter(o => o.id !== order.id));
      showSnackbar('Orden restaurada', 'success');
      logActivity(session?.user.email, 'RESTAURAR_ORDEN', { order_id: order.id, tiendanube_id: order.tiendanube_id, contact_name: order.contact_name });
      fetchOrders();
    } catch (err) {
      console.error('Error restoring order:', err);
      showSnackbar('Error al restaurar la orden', 'error');
    }
  }

  async function permanentlyDeleteOrder(order: Order) {
    const displayId = getOrderDisplayId(order);
    if (!window.confirm(`¿Eliminar DEFINITIVAMENTE la orden #${displayId}? Esta acción no se puede deshacer.`)) return;
    try {
      const { error } = await supabase.from('orders').delete().eq('id', order.id);
      if (error) throw error;
      setTrashedOrders(prev => prev.filter(o => o.id !== order.id));
      showSnackbar('Orden eliminada definitivamente', 'success');
      logActivity(session?.user.email, 'ELIMINAR_DEFINITIVO_ORDEN', { order_id: order.id, tiendanube_id: order.tiendanube_id, contact_name: order.contact_name });
    } catch (err) {
      console.error('Error permanently deleting order:', err);
      showSnackbar('Error al eliminar la orden', 'error');
    }
  }

  // ===== MÓDULO CABIFY (encapsulado): etiquetas desde la card =====
  const loadCabifyMap = useCallback(async () => {
    if (!CABIFY_ENABLED) return;
    // Todo menos los estados muertos (borrado/devuelto/cancelado/falló retiro): el webhook
    // va pisando status (intransit, delivered...) y la card sigue mostrando el envío; si el
    // envío murió, la card vuelve a ofrecer "Generar etiqueta" para re-despachar.
    const { data } = await supabase.from('cabify_deliveries')
      .select('order_id, parcel_id, status, external_id')
      .not('status', 'in', `(${CABIFY_DEAD.join(',')})`)
      .order('created_at', { ascending: true });
    // Una orden puede tener VARIOS parcels (multi-bulto): agrupamos todos y el estado
    // mostrado es el más avanzado de la progresión.
    const PROG = ['created', 'ready', 'shipped', 'readytopickup', 'qualifiedforpickup', 'onroutetopickup', 'pickingup', 'onroutetofinalhub', 'receivedonfinalhub', 'readytodispatch', 'intransit', 'onroutetodelivery', 'delivering', 'undelivered', 'returning', 'delivered'];
    const m = new Map<number, { parcels: { parcel_id: string; status: string; label?: string }[]; status: string }>();
    (data || []).forEach((d: any) => {
      const e = m.get(d.order_id) || { parcels: [] as { parcel_id: string; status: string; label?: string }[], status: d.status as string };
      e.parcels.push({ parcel_id: d.parcel_id, status: d.status, label: (String(d.external_id || '').match(/\d+\/\d+/) || [''])[0] });
      if (PROG.indexOf(d.status) > PROG.indexOf(e.status)) e.status = d.status;
      m.set(d.order_id, e);
    });
    setCabifyMap(m);
  }, []);
  useEffect(() => { loadCabifyMap(); }, [loadCabifyMap]);

  async function cabifyOpenLabelPdf(parcelIds: string | string[]) {
    const ids = Array.isArray(parcelIds) ? parcelIds : [parcelIds];
    const { data, error } = await supabase.functions.invoke('cabify-logistics', {
      body: { action: 'labels', parcel_ids: ids },
    });
    if (error || !data?.ok) throw new Error(data?.error || error?.message || 'No se pudo bajar la etiqueta');
    const bytes = atob(data.pdf_base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    window.open(URL.createObjectURL(new Blob([arr], { type: 'application/pdf' })), '_blank');
  }

  async function cabifyGenerate(order: Order) {
    setCabifyBusyId(order.id);
    try {
      // A Cabify NUNCA le mandamos solo texto: la ubicamos con Google y validamos CP/altura
      // (ver src/geocode.ts — caso #4650, Cabify la geocodificó a 16 km).
      const geo = await ensureOrderCoords(order as any);
      if (!geo.ok) {
        showSnackbar(`Cabify: revisá la dirección antes de generar la etiqueta — ${geo.reason}${geo.found ? `. Google la entendió como: ${geo.found}` : ''}. Corregila en Editar eligiendo una sugerencia.`, 'error');
        return;
      }
      setOrders(prev => prev.map(o => (o.id === order.id ? ({ ...o, delivery_lat: geo.lat, delivery_lon: geo.lon } as any) : o)));
      const { data, error } = await supabase.functions.invoke('cabify-logistics', {
        body: {
          action: 'create_batch',
          created_by: session?.user?.email,
          orders: [{ order_id: order.id, order_display: getOrderDisplayId(order), dropoff_addr: order.delivery_address, dropoff_lat: geo.lat, dropoff_lon: geo.lon, dropoff_name: order.contact_name, dropoff_phone: order.contact_phone || (order as any).canje_phone, instr: order.delivery_unit ? `Piso/depto: ${order.delivery_unit}` : undefined }],
        },
      });
      const r = data?.results?.[0];
      if (r && !r.ok && Array.isArray(r.existing) && r.existing.length) {
        // Ya estaba generada (otra pestaña, otro usuario, doble click): abrimos la existente.
        await cabifyOpenLabelPdf(r.existing);
        await loadCabifyMap();
        showSnackbar('Ya tenía etiqueta generada — te la abrí para imprimir', 'success');
        return;
      }
      if (error || !data?.ok || !r?.ok) throw new Error(r?.error || data?.error || error?.message || 'No se pudo generar la etiqueta');
      await cabifyOpenLabelPdf(r.parcel_ids || [r.parcel_id]);
      await loadCabifyMap();
      showSnackbar(r.bultos > 1 ? `${r.bultos} etiquetas Cabify generadas (multi-bulto)` : 'Etiqueta Cabify generada', 'success');
    } catch (e) {
      showSnackbar(`Cabify: ${String((e as Error).message || e)}`, 'error');
    } finally {
      setCabifyBusyId(null);
    }
  }

  async function cabifyPrint(order: Order) {
    const d = cabifyMap.get(order.id);
    if (!d) return;
    setCabifyBusyId(order.id);
    try {
      await cabifyOpenLabelPdf(d.parcels.map(p => p.parcel_id));
    } catch (e) {
      showSnackbar(`Cabify: ${String((e as Error).message || e)}`, 'error');
      // Si la etiqueta murió en Cabify (borrada desde el portal), la función ya marcó
      // la fila — refrescamos para que la card vuelva a ofrecer "Generar etiqueta".
      await loadCabifyMap();
    } finally {
      setCabifyBusyId(null);
    }
  }

  // "+ Bulto" (29/10/2026): pedido grande que se separa en más cajas → otra etiqueta Cabify igual
  // (misma dirección/coordenadas y destinatario) con cabify-logistics add_parcel. Abre SOLO la nueva.
  // Las etiquetas ya impresas no se renumeran: la nueva sale "N/N" (ej. 3/3).
  async function cabifyAddParcel(order: Order) {
    const d = cabifyMap.get(order.id);
    if (!d) return;
    const next = d.parcels.length + 1;
    if (!window.confirm(`¿Agregar otro bulto a ${getOrderDisplayId(order)}? Se genera una etiqueta Cabify más (${next}° bulto) a la misma dirección.${d.status !== 'created' ? '\n\nOjo: el pedido ya está despachado — el bulto nuevo hay que despacharlo desde Envíos Cabify.' : ''}`)) return;
    setCabifyBusyId(order.id);
    try {
      const { data, error } = await supabase.functions.invoke('cabify-logistics', {
        body: { action: 'add_parcel', order_id: order.id, order_display: getOrderDisplayId(order), created_by: session?.user?.email },
      });
      if (error || !data?.ok) throw new Error(data?.error || error?.message || 'No se pudo agregar el bulto');
      await cabifyOpenLabelPdf([data.parcel_id]);
      await loadCabifyMap();
      logActivity(session?.user?.email, 'ETIQUETA_CABIFY_AGREGADA', { order_id: order.id, note: `Bulto extra: etiqueta ${data.external_id || ''} (pedido separado en más cajas)` });
      showSnackbar(`Etiqueta del bulto ${data.total_etiquetas || next} generada${d.status !== 'created' ? ' — despachala desde Envíos Cabify' : ''}`, 'success');
    } catch (e) {
      showSnackbar(`Cabify: ${String((e as Error).message || e)}`, 'error');
    } finally {
      setCabifyBusyId(null);
    }
  }

  async function cabifyDeleteLabel(order: Order) {
    const d = cabifyMap.get(order.id);
    if (!d) return;
    const created = d.parcels.filter(p => p.status === 'created').map(p => p.parcel_id);
    if (!created.length) return;
    if (!window.confirm(`¿Eliminar ${created.length > 1 ? `las ${created.length} etiquetas` : 'la etiqueta'} Cabify de este pedido? (solo se puede antes de despachar)`)) return;
    setCabifyBusyId(order.id);
    try {
      const { data, error } = await supabase.functions.invoke('cabify-logistics', {
        body: { action: 'delete_parcels', parcel_ids: created },
      });
      if (error || !data?.ok) throw new Error(data?.error || error?.message || 'No se pudo eliminar');
      await loadCabifyMap();
      showSnackbar('Etiqueta Cabify eliminada', 'success');
    } catch (e) {
      showSnackbar(`Cabify: ${String((e as Error).message || e)}`, 'error');
    } finally {
      setCabifyBusyId(null);
    }
  }
  // ===== fin MÓDULO CABIFY =====


  // Opciones de envío únicas + count, para el multiselect de filtro.
  const shippingOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of orders) {
      const key = ((o as any).shipping_option || '').trim() || '(sin envío)';
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));
  }, [orders]);


  const filteredOrders = orders.filter(order => {
    // Tab logic
    if (activeTab === 'orders' && order.status === 'presupuesto') return false;
    if (activeTab === 'budgets' && order.status !== 'presupuesto') return false;

    // Date range logic
    if (filterDateRange === 'specific') {
      // día calendario exacto (hora local)
      if (filterSpecificDate) {
        const d = new Date(order.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (key !== filterSpecificDate) return false;
      }
    } else if (filterDateRange !== 'all') {
      const days = parseInt(filterDateRange, 10);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      if (new Date(order.created_at) < cutoff) {
        return false;
      }
    }

    // Filter logic
    const matchesStatus = filterStatus.size === 0 || filterStatus.has(filterStatusOf(order)); // 'empaquetado' = recientes; viejas aparte

    const cleanSearchTerm = searchTerm.trim();

    // Hide delivered and cancelled orders by default unless "Ver entregadas / canceladas" is checked, specifically filtering, or searching
    // con fecha específica elegida se muestran TODAS las de ese día (incl. entregadas/canceladas)
    const specificDateActive = filterDateRange === 'specific' && filterSpecificDate !== '';
    if (activeTab === 'orders' && !showDelivered && !specificDateActive && filterStatus.size === 0 && cleanSearchTerm === '' && filterCanje !== 'canje' && (order.status === 'entregado' || order.status === 'cancelada' || order.status === 'stock')) {
      return false;
    }
    const isPending = order.payment_status === 'pending' || order.payment_status === 'voided';
    const isCashOrder = isPending && order.payment_details?.method === 'custom';
    const isWireTransfer = isPending && order.payment_details?.method === 'wire_transfer';
    const matchesPayment = filterPaymentStatus === 'all' ||
      (filterPaymentStatus === 'pago' && order.payment_status === 'paid') ||
      (filterPaymentStatus === 'pendiente' && isPending && !isCashOrder && !isWireTransfer) ||
      (filterPaymentStatus === 'pendiente_efectivo' && isCashOrder) ||
      (filterPaymentStatus === 'pendiente_transferencia' && isWireTransfer);

    const shippingKey = ((order as any).shipping_option || '').trim() || '(sin envío)';
    const matchesShipping = filterShipping.size === 0 || filterShipping.has(shippingKey);

    const deadlineStatus = getDeadlineStatus(order);
    const matchesDeadline = filterDeadline === 'all' ||
      (filterDeadline === 'late' && deadlineStatus === 'late') ||
      (filterDeadline === 'due_soon' && deadlineStatus === 'due_soon') ||
      (filterDeadline === 'fabricacion' && requiresFabrication(order) && ['nueva', 'cortado', 'falta_insumo'].includes(order.status));

    const isCanje = !!(order as any).is_canje;
    const matchesCanje = filterCanje === 'all' || (filterCanje === 'canje' ? isCanje : !isCanje);

    const matchesFilter = matchesStatus && matchesPayment && matchesShipping && matchesDeadline && matchesCanje;

    const normalizeText = (text: string) =>
      text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    const normalizedSearch = normalizeText(cleanSearchTerm);
    const normalizedName = normalizeText(order.contact_name || '');
    const orderId = getOrderDisplayId(order);

    const matchesProductName = Array.isArray(order.products) && order.products.some((p: any) =>
      p.name && normalizeText(p.name).includes(normalizedSearch)
    );

    const phoneDigits = (order.contact_phone || '').replace(/\D/g, '');
    const searchDigits = cleanSearchTerm.replace(/\D/g, '');
    const matchesPhone = searchDigits.length >= 3 && phoneDigits.includes(searchDigits);

    const matchesCabify = !!cabifySearchIds && cabifySearchIds.has(Number(order.id));
    const matchesSearch = normalizedName.includes(normalizedSearch) || orderId.includes(cleanSearchTerm) || matchesProductName || matchesPhone || matchesCabify;

    // Only apply status filter in Orders tab. In Budgets tab, status is always 'presupuesto' so filter is redundant unless we have budget statuses.
    if (activeTab === 'budgets') return matchesSearch;

    return matchesFilter && matchesSearch;
  }).sort((a, b) => {
    if (sortDateOrder === 'ready') {
      // Misma prioridad que el taller: urgentes, después fecha de listo más próxima, después la más vieja.
      const u = Number(!!b.is_urgent) - Number(!!a.is_urgent);
      if (u) return u;
      const ra = a.ready_by || '9999-12-31', rb = b.ready_by || '9999-12-31';
      if (ra !== rb) return ra < rb ? -1 : 1;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    const timeA = new Date(a.created_at).getTime();
    const timeB = new Date(b.created_at).getTime();
    return sortDateOrder === 'asc' ? timeA - timeB : timeB - timeA;
  });


  // (Las sub-barras de Productos / Producción / Clientes / Órdenes se reemplazaron por el
  //  menú lateral — src/ui/AppSidebar.tsx — donde cada pantalla es un ítem con su URL.)

  // Búsqueda SOLO con Enter (21/10/2026; en vivo con 300 ms trababa la grilla). El texto que se
  // tipea vive en <OrderSearchBox> (estado propio): escribir no re-renderiza el admin entero.
  // Al buscar se limpian estado/pago para que encuentre la orden esté donde esté.
  const applySearch = (raw: string) => {
    const val = raw.trim();
    setSearchTerm(val);
    if (val) { setFilterStatus(new Set()); setFilterPaymentStatus('all'); setShowDelivered(false); }
  };
  // Conteo por estado para los badges (sobre TODAS las órdenes, no sobre las filtradas: si no,
  // al tocar un estado los demás badges desaparecían).
  const chipCounts = orders.reduce((acc, o) => {
    if (o.status !== 'presupuesto') { const k = filterStatusOf(o); acc[k] = (acc[k] || 0) + 1; }
    return acc;
  }, {} as Record<string, number>);
  const anyFilter = filterStatus.size > 0 || filterPaymentStatus !== 'all' || filterDateRange !== 'all'
    || filterDeadline !== 'all' || filterCanje !== 'all' || filterShipping.size > 0 || showDelivered || !!searchTerm;
  const clearFilters = () => {
    setFilterStatus(new Set()); setFilterPaymentStatus('all'); setFilterDateRange('all'); setFilterDeadline('all'); setFilterCanje('all');
    setFilterShipping(new Set()); setShowDelivered(false); setSearchTerm('');
  };

  if (!session) {
    return <Login />;
  }
  if (twoFA !== 'ok') {
    return twoFA === 'needed'
      ? <TwoFactor emailHint={twoFAHint || session.user.email || ''} onVerified={() => setTwoFA('ok')} />
      : null;
  }

  // Rol "canje" (Rafa, 13/10/2026): solo carga canjes para creadores (src/CanjeHome.tsx).
  if (role === 'canje') {
    return <CanjeHome email={session.user.email} />;
  }
  // Esperar el rol antes de mostrar el admin: sin esto un usuario 'canje' veía el admin un instante.
  if (role === null) return null;

  // Rol "staff" (Rafa, 20/10/2026): opera el admin (órdenes, atención, envíos, taller, clientes)
  // pero NO ve plata del negocio: Costos & BI, gastos, La Suite, catálogo/insumos/mayorista
  // (costos y márgenes), ni el costo de WhatsApp en Atención. Ojo: es un límite de la UI.
  const isStaff = role === 'staff';
  if (isStaff && STAFF_HIDDEN_TABS.includes(activeTab as string)) return null; // el effect lo manda a Órdenes

  // Rol "creator" (era el equipo de contenido): la sección Contenido se eliminó el 10/10/2026,
  // así que estos usuarios ya no tienen nada que ver en el admin.
  if (role === 'creator') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center' }}>
        <div>
          <img src="/logo.png" alt="Krilic" style={{ height: 28, marginBottom: 16 }} />
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 16px' }}>Tu usuario no tiene acceso al admin.</p>
          <button className="btn-primary-header" onClick={() => supabase.auth.signOut({ scope: 'local' })}><LogOut size={16} /> Cerrar sesión</button>
        </div>
      </div>
    );
  }

  // Ya no hay pantalla completa de carga: el menú y la pantalla aparecen al instante; la grilla
  // de órdenes muestra tarjetas esqueleto y Costos & BI espera al historial (ver fetchOrders).

  if (window.location.pathname === '/whatsapp') {
    return (
      <div className="dashboard-container">
        <header className="main-header">
          <div className="header-content">
            <div className="logo-section">
              <img src="/logo.png" alt="Krilic" style={{ height: '28px', objectFit: 'contain' }} />
            </div>
            <div className="header-right">
              <button className="btn-primary-header" onClick={() => window.location.href = '/'}>
                Volver al Inicio
              </button>
            </div>
          </div>
        </header>

        <main className="main-content">
          <WhatsAppTest />
        </main>
      </div>
    );
  }

  if (window.location.pathname === '/cortes') {
    return <Nesting />;
  }

  return (
    <AppSidebar
      groups={buildNav({ escalated: escalatedCount, notes: notesCount, trash: trashedOrders.length, meliConnected, staff: isStaff })}
      active={activeTab}
      onNavigate={(k) => setActiveTab(k as any)}
      onNewSale={() => setShowNewOrder(true)}
      onNewExpense={isStaff ? undefined : () => { setActiveTab('costs'); setOpenExpenseModal(true); }}
      onNewCanje={isStaff ? () => { setNewOrderCanje(true); setShowNewOrder(true); } : undefined}
      onSync={handleSync}
      syncing={loading}
      tnConnected={tnConnected}
      meliConnected={meliConnected}
      connectionsKnown={connChecked}
      meliNickname={meliNickname}
      onConnectTn={() => { window.location.href = 'https://www.tiendanube.com/apps/26338/authorize'; }}
      onConnectMeli={() => { window.location.href = 'https://auth.mercadolibre.com.ar/authorization?response_type=code&client_id=2607846649216767&redirect_uri=' + (process.env.REACT_APP_SUPABASE_URL || '').trim() + '/functions/v1/meli-auth-callback'; }}
      bell={<PushBell userId={session?.user?.id} email={session?.user?.email} role={role} notify={(m) => showSnackbar(m, 'success')} />}
      email={session?.user?.email}
      onLogout={() => supabase.auth.signOut({ scope: 'local' })}
    >

      <main className="main-content">
        <div className="controls-bar">

          {/* Buscador y filtros: SOLO en Órdenes y Presupuestos. Antes era una lista de pestañas
              EXCLUIDAS y cada pantalla nueva lo heredaba sin querer (Proveedores, Mayorista, Cabify). */}
          {(activeTab === 'orders' || activeTab === 'budgets') && (
            // Rediseño 10/10/2026 (uiarc / tokens .tw): buscador a la izquierda, estados como
            // badges clickeables a la derecha (reemplazan el desplegable de estados), filtros abajo.
            <div className="tw w-full min-w-0">
              {activeTab === 'orders' && (
                <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Órdenes</h1>
                    <p className="mt-1 text-[length:var(--text-sm)] text-soft">
                      {ordersLoading ? 'Cargando órdenes…' : <>{filteredOrders.length} {filteredOrders.length === 1 ? 'orden' : 'órdenes'}{anyFilter ? ' con estos filtros' : ' abiertas'}{!historyLoaded && ' · cargando historial…'}</>}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedPrintOrders.size > 0 && (<>
                      <button type="button" disabled={printingSelection} title="Imprimir etiquetas seleccionadas (2 por hoja)"
                        onClick={async () => {
                          const orderIdSet = new Set(filteredOrders.map(o => o.id));
                          const ordersToPrint = orders.filter(o => selectedPrintOrders.has(o.id) && orderIdSet.has(o.id));
                          if (ordersToPrint.length === 0) return;
                          setPrintingSelection(true);
                          try { await generateCompactThermalLabelsPDF(ordersToPrint, 2); }
                          catch (err) { console.error('Error generando PDF de selección:', err); showSnackbar('Error al generar el PDF', 'error'); }
                          finally { setPrintingSelection(false); }
                        }}
                        className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius-control)] bg-[var(--foreground)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--card)] disabled:opacity-60">
                        <Printer size={15} /> Imprimir {selectedPrintOrders.size} (2/hoja)
                      </button>
                      <button type="button" onClick={() => setSelectedPrintOrders(new Set())}
                        className="inline-flex h-9 items-center rounded-[var(--radius-control)] border border-line px-3 text-[length:var(--text-sm)] text-soft hover:bg-muted hover:text-ink">
                        Quitar selección
                      </button>
                    </>)}
                    {/* MÓDULO CABIFY (encapsulado): envíos en lote de los empaquetados calificados */}
                    {CABIFY_ENABLED && (() => {
                      const n = orders.filter(o => o.status === 'empaquetado' && cabifyQualifies(o)).length;
                      return (
                        <button type="button" onClick={() => setShowCabifyBatch(true)}
                          title="Envíos Cabify en lote: crear + etiquetas + despachar los empaquetados calificados"
                          className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius-control)] border border-line bg-card px-3 text-[length:var(--text-sm)] font-medium text-ink hover:bg-muted">
                          <Truck size={15} /> Envíos Cabify{n ? <span className="rounded-full bg-[var(--foreground)] px-1.5 text-[length:var(--text-xs)] text-[var(--card)]">{n}</span> : null}
                        </button>
                      );
                    })()}
                  </div>
                </header>
              )}

              {/* Buscador (izquierda) + estados (derecha) */}
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <OrderSearchBox term={searchTerm} onApply={applySearch} onClear={() => setSearchTerm('')}
                  placeholder={activeTab === 'budgets' ? 'Buscar presupuesto por nombre o producto… (Enter)' : 'Buscar por nombre, nº de pedido, producto, teléfono o ID de Cabify… (Enter)'} />
                {activeTab === 'orders' && (
                  <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filtrar por estado">
                    {CHIP_STATUS.filter(st => chipCounts[st]).map(st => {
                      const on = filterStatus.has(st);
                      const closed = st === 'entregado' || st === 'cancelada' || st === 'stock';
                      return (
                        <button key={st} type="button" aria-pressed={on}
                          title={on ? 'Quitar este filtro' : st === 'empaquetado_viejo' ? `Empaquetadas hace más de ${OLD_PACKED_DAYS} días (no retiradas / en el limbo). El estado sigue siendo Empaquetado.` : `Ver solo ${(STATUS_LABELS[st] || st).toLowerCase()}`}
                          onClick={() => {
                            const next = new Set(filterStatus);
                            if (next.has(st)) next.delete(st); else next.add(st);
                            setFilterStatus(next);
                          }}
                          className={`stat-card stat-${st} !m-0 cursor-pointer ${on ? 'outline outline-2 outline-offset-1 outline-[var(--foreground)]' : ''} ${closed && !on ? 'opacity-60' : ''}`}>
                          {/* outline y no ring: sin la capa base de Tailwind, ring-* no dibuja nada */}
                          <span className="tabular-nums">{chipCounts[st].toLocaleString('es-AR')}</span> {CHIP_LABELS[st] || STATUS_LABELS[st]}
                          {on && <X size={12} className="-mr-0.5" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Filtros */}
              {activeTab === 'orders' && (
                <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-surface)] border border-line bg-card p-2">
                  {(() => {
                    const SEL = 'h-9 cursor-pointer rounded-[var(--radius-control)] border border-line bg-card px-2 text-[length:var(--text-sm)] text-ink hover:border-[var(--border-strong)] focus:border-[var(--foreground)] focus:outline-none';
                    const on = (active: boolean) => (active ? ' !border-[var(--foreground)] font-medium' : '');
                    return (<>
                      <select aria-label="Fecha de compra" className={SEL + on(filterDateRange !== 'all')} value={filterDateRange} onChange={(e) => {
                        setFilterDateRange(e.target.value);
                        if (e.target.value !== 'all') { setSearchTerm(''); }
                      }}>
                        <option value="all">Cualquier fecha</option>
                        <option value="7">Últimos 7 días</option>
                        <option value="30">Últimos 30 días</option>
                        <option value="60">Últimos 60 días</option>
                        <option value="90">Últimos 90 días</option>
                        <option value="specific">Un día…</option>
                      </select>
                      {filterDateRange === 'specific' && (
                        <input type="date" aria-label="Fecha específica" value={filterSpecificDate} onChange={(e) => setFilterSpecificDate(e.target.value)} className={SEL} />
                      )}
                      <select aria-label="Pago" className={SEL + on(filterPaymentStatus !== 'all')} value={filterPaymentStatus} onChange={(e) => {
                        setFilterPaymentStatus(e.target.value);
                        if (e.target.value !== 'all') { setSearchTerm(''); }
                      }}>
                        <option value="all">Todos los pagos</option>
                        <option value="pago">Pagados</option>
                        <option value="pendiente">Pendientes</option>
                        <option value="pendiente_efectivo">Pend. efectivo</option>
                        <option value="pendiente_transferencia">Pend. transferencia</option>
                      </select>
                      <select aria-label="Canjes" className={SEL + on(filterCanje !== 'all')} value={filterCanje} onChange={(e) => setFilterCanje(e.target.value)}>
                        <option value="all">Canjes: todas</option>
                        <option value="canje">🎁 Solo canjes ({orders.filter(o => (o as any).is_canje && o.status !== 'presupuesto').length})</option>
                        <option value="sin_canje">Sin canjes</option>
                      </select>
                      <select aria-label="Fecha de listo" className={SEL + on(filterDeadline !== 'all')} value={filterDeadline} onChange={(e) => setFilterDeadline(e.target.value)}>
                        <option value="all">Fecha de listo</option>
                        <option value="fabricacion">Con fabricación</option>
                        <option value="due_soon">Vencen en 2 días</option>
                        <option value="late">Atrasadas</option>
                      </select>
                      <div className="relative" tabIndex={-1} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setShowShippingDropdown(false); }}>
                        <button type="button" className={SEL + on(filterShipping.size > 0) + ' inline-flex items-center gap-1.5'} onClick={() => setShowShippingDropdown(!showShippingDropdown)}>
                          {filterShipping.size === 0 ? 'Todos los envíos' : `${filterShipping.size} tipo(s) de envío`}
                          <ChevronDown size={14} className="text-faint" />
                        </button>
                        {showShippingDropdown && (
                          <div className="absolute left-0 z-30 mt-1 max-h-80 w-72 overflow-y-auto rounded-[var(--radius-panel)] border border-line bg-card p-1 shadow-[var(--shadow-floating)]">
                            {filterShipping.size > 0 && (
                              <button type="button" onClick={() => setFilterShipping(new Set())}
                                className="w-full rounded-[calc(var(--radius-control)-2px)] px-2 py-1.5 text-left text-[length:var(--text-xs)] font-medium text-ink hover:bg-muted">Quitar filtro de envío</button>
                            )}
                            {shippingOptions.map(({ label, count }) => (
                              <label key={label} title={label} className="flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius-control)-2px)] px-2 py-1.5 text-[length:var(--text-sm)] text-ink hover:bg-muted">
                                <input type="checkbox" checked={filterShipping.has(label)} onChange={() => {
                                  const next = new Set(filterShipping);
                                  if (next.has(label)) next.delete(label); else next.add(label);
                                  setFilterShipping(next);
                                }} className="h-4 w-4 accent-[var(--foreground)]" />
                                <span className="min-w-0 flex-1 truncate">{label}</span>
                                <span className="text-[length:var(--text-xs)] tabular-nums text-faint">{count}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <label className="ml-1 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-[length:var(--text-sm)] text-soft" title="Incluye las entregadas y canceladas (por defecto se ocultan)">
                        <input type="checkbox" checked={showDelivered} className="h-4 w-4 accent-[var(--foreground)]"
                          onChange={(e) => { setShowDelivered(e.target.checked); if (e.target.checked) { setSearchTerm(''); } }} />
                        Ver cerradas
                      </label>
                      <div className="ml-auto flex items-center gap-2">
                        <select aria-label="Orden" className={SEL} value={sortDateOrder} onChange={(e) => setSortDateOrder(e.target.value)}>
                          <option value="desc">Más recientes</option>
                          <option value="asc">Más antiguas</option>
                          <option value="ready">Por prioridad</option>
                        </select>
                        {anyFilter && (
                          <button type="button" onClick={clearFilters}
                            className="inline-flex h-9 items-center gap-1 rounded-[var(--radius-control)] px-2.5 text-[length:var(--text-sm)] font-medium text-ink hover:bg-muted">
                            <X size={14} /> Limpiar filtros
                          </button>
                        )}
                      </div>
                    </>);
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        {activeTab === 'products' ? (
          <Products showSnackbar={showSnackbar} section="catalog" />
        ) : activeTab === 'providers' ? (
          <Products showSnackbar={showSnackbar} section="providers" />
        ) : activeTab === 'costs' ? (
          // El BI necesita TODAS las órdenes: con solo las abiertas los números saldrían mal.
          !historyLoaded ? (
            <div className="empty-state"><div className="spinner" /><p>Cargando el historial de órdenes para el BI…</p></div>
          ) : (
            <Costs userEmail={session?.user?.email} orders={ordersNoCanje} canjeOrders={canjeOrders} openExpenseModal={openExpenseModal} onExpenseModalClosed={() => setOpenExpenseModal(false)} />
          )
        ) : activeTab === 'imports' ? (
          <Imports userEmail={session?.user?.email} />
        ) : activeTab === 'circulo_rojo' ? (
          <CirculoRojo userEmail={session?.user?.email} />
        ) : activeTab === 'reviews' ? (
          <Reviews userEmail={session?.user?.email} />
        ) : activeTab === 'carritos' ? (
          <AbandonedCarts showSnackbar={showSnackbar} />
        ) : activeTab === 'consignment' ? (
          <Consignment userEmail={session?.user?.email} />
        ) : activeTab === 'taller' ? (
          <Taller />
        ) : activeTab === 'cabify' ? (
          <CabifyScreen />
        ) : activeTab === 'mapa' ? (
          <MapaMensajes />
        ) : activeTab === 'despacho' ? (
          <ControlDespacho userEmail={session?.user?.email} />
        ) : activeTab === 'dhl' ? (
          <Dhl />
        ) : activeTab === 'wholesale' ? (
          <Wholesale showSnackbar={showSnackbar} />
        ) : activeTab === 'insumos' ? (
          <Insumos />
        ) : activeTab === 'notes' ? (
          <Notes onNotesChange={fetchNotesCount} />
        ) : activeTab === 'activity' ? (
          <ActivityLog />
        ) : activeTab === 'meli' ? (
          <MeliListings showSnackbar={showSnackbar} />
        ) : activeTab === 'transfers' ? (
          <InboxView
            hideBilling={isStaff}
            orders={orders as any}
            showSnackbar={showSnackbar}
            onOrderClick={(orderId) => {
              const order = orders.find(o =>
                orderId.startsWith('K-')
                  ? o.id === parseInt(orderId.substring(2))
                  : getOrderDisplayId(o) === orderId || String(o.tiendanube_id) === String(orderId)
              );
              if (order) setEditingOrder(order);
              else alert('Pedido no encontrado en los registros recientes.');
            }}
          />
        ) : activeTab === 'print' ? (
          <PrintOrdersTool />
        ) : activeTab === 'trash' ? (
          <div className="orders-grid">
            {trashLoading ? (
              <div className="empty-state"><p>Cargando papelera...</p></div>
            ) : trashedOrders.length === 0 ? (
              <div className="empty-state">
                <Trash2 size={48} />
                <p>La papelera está vacía.</p>
              </div>
            ) : (
              trashedOrders.map(order => {
                const displayId = getOrderDisplayId(order);
                const deletedAtStr = order.deleted_at
                  ? new Date(order.deleted_at).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
                  : '';
                return (
                  <div key={order.id} className="order-card" style={{ opacity: 0.85 }}>
                    <div className="order-header">
                      <div>
                        <h3 style={{ margin: 0 }}>#{displayId} · {order.contact_name}</h3>
                        <p className="order-meta" style={{ marginTop: 4 }}>
                          {order.source || 'manual'} · creada {new Date(order.created_at).toLocaleDateString('es-AR')}
                          {deletedAtStr && <> · <span style={{ color: 'var(--danger, #dc2626)' }}>eliminada {deletedAtStr}</span></>}
                        </p>
                      </div>
                    </div>
                    <div className="actions-footer">
                      <button className="action-btn edit-btn" onClick={() => restoreOrder(order)}>
                        <RefreshCw size={16} /> Restaurar
                      </button>
                      <button className="action-btn delete-btn" onClick={() => permanentlyDeleteOrder(order)}>
                        <Trash2 size={16} /> Eliminar definitivo
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : activeTab === 'orders' ? (
          <div className="orders-grid">
            {ordersLoading && orders.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={`sk-${i}`} className="order-card" aria-hidden="true">
                  <div className="order-skel" style={{ width: '45%', height: 18 }} />
                  <div className="order-skel" style={{ width: '70%', height: 22, marginTop: 14 }} />
                  <div className="order-skel" style={{ width: '35%', height: 14, marginTop: 8 }} />
                  <div className="order-skel" style={{ width: '100%', height: 60, marginTop: 18 }} />
                  <div className="order-skel" style={{ width: '100%', height: 90, marginTop: 12 }} />
                </div>
              ))
            ) : filteredOrders.length === 0 ? (
              <div className="empty-state">
                <AlertCircle size={48} />
                <p>No se encontraron órdenes con esos filtros.</p>
              </div>
            ) : (
              filteredOrders.map(order => (
                <div key={order.id} className={`order-card status-border-${order.status}`}>
                  <div className="order-header">
                    <div className="order-header-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={selectedPrintOrders.has(order.id)}
                        onChange={(e) => {
                          setSelectedPrintOrders(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(order.id); else next.delete(order.id);
                            return next;
                          });
                        }}
                        title="Seleccionar para imprimir"
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#991b1b' }}
                      />
                      <span className="order-id">
                        {getOrderDisplayId(order).startsWith('K-') ? getOrderDisplayId(order) : `#${getOrderDisplayId(order)}`}
                      </span>
                    </div>
                    <div className={`status-badge ${order.status}`}>
                      {STATUS_LABELS[order.status] || order.status}
                    </div>
                    {readyChip(order)}
                    {order.source && (
                      <div className={`source-badge ${order.source.toLowerCase()}`}>
                        {order.source === 'tiendanube' && 'Tiendanube'}
                        {order.source === 'mercadolibre' && 'MercadoLibre'}
                        {order.source === 'woocommerce' && 'Boutique'}
                        {order.source === 'whatsapp' && 'WhatsApp'}
                        {order.source === 'ig' && 'Instagram'}
                        {order.source === 'showroom' && 'Showroom'}
                        {!['tiendanube', 'mercadolibre', 'woocommerce', 'whatsapp', 'ig', 'showroom'].includes(order.source) && order.source}
                      </div>
                    )}
                    {(() => {
                      // TN: se deduce del gateway. Ventas propias: lo que se eligió en el popup.
                      const manualPm = String(order.payment_details?.payment_method || '').toLowerCase();
                      const pm = tnPaymentMethod(order)
                        ?? (['efectivo', 'transferencia', 'tarjeta'].includes(manualPm) && !['mercadolibre', 'woocommerce'].includes(order.source || '') ? manualPm as 'efectivo' | 'transferencia' | 'tarjeta' : null);
                      if (!pm) return null;
                      // a qué cuenta va la transferencia (obligatorio en ventas manuales desde 10/10/2026)
                      const acc = pm === 'transferencia' ? order.payment_details?.transfer_account : null;
                      const cfg = {
                        efectivo: { label: 'Efectivo', icon: '💵', bg: 'rgba(22,163,74,0.14)', color: '#16a34a' },
                        transferencia: { label: 'Transferencia', icon: '🏦', bg: 'rgba(29,78,216,0.14)', color: '#1d4ed8' },
                        tarjeta: { label: 'Tarjeta', icon: '💳', bg: 'rgba(124,58,237,0.14)', color: '#7c3aed' },
                      }[pm];
                      return (
                        <div className="source-badge" title={`Medio de pago: ${cfg.label}${acc ? ` a la cuenta ${acc === 'krilic' ? 'de Krilic' : 'de la financiera'}` : ''}`} style={{ background: cfg.bg, color: cfg.color }}>
                          {cfg.icon} {cfg.label}{acc ? ` · ${acc === 'krilic' ? 'Krilic' : 'Financiera'}` : ''}
                        </div>
                      );
                    })()}
                    {/* Correo en viaje (22/10/2026): la orden pasa a entregado recién cuando TN informa la entrega */}
                    {order.carrier_status && order.carrier_status !== 'DELIVERED' && order.status !== 'entregado' && order.status !== 'cancelada' && (
                      <div className="source-badge"
                        title={`Según Tiendanube: ${order.carrier_status}${order.carrier_shipped_at ? ` · despachado el ${new Date(order.carrier_shipped_at).toLocaleDateString('es-AR')}` : ''}. Pasa a entregado (y arranca el plazo de la review) cuando el correo lo entregue.`}
                        style={{ background: '#e0e7ff', color: '#3730a3' }}>
                        {order.carrier_status === 'READY_FOR_PICKUP' ? '📍 En punto de retiro' : '📮 En viaje con el correo'}
                        {order.carrier_shipped_at ? ` · ${new Date(order.carrier_shipped_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}` : ''}
                      </div>
                    )}
                    {order.edited_locally && (
                      <div
                        className="source-badge"
                        title={`Editada localmente${order.edited_at ? ' el ' + new Date(order.edited_at).toLocaleString('es-AR') : ''}. Ya no coincide con la fuente original.`}
                        style={{ background: '#f59e0b', color: '#fff' }}
                      >
                        ✏️ Editada
                      </div>
                    )}
                    {order.needs_payment_followup && (
                      <div
                        className="source-badge"
                        title={`Cliente eligió envío pero pagó efectivo. Hay que contactarlo para cambiar el medio de pago.${order.payment_followup_at ? ' Pendiente desde ' + new Date(order.payment_followup_at).toLocaleString('es-AR') : ''}`}
                        style={{ background: '#dc2626', color: '#fff' }}
                      >
                        💰 Cambio de pago
                      </div>
                    )}
                  </div>

                  <div className="order-client">
                    <div className="client-main">
                      <h3 className="order-name">{order.contact_name}</h3>
                      <div className="order-date">
                        <Clock size={14} />
                        <span>{formatDate(order.created_at)}</span>
                      </div>
                    </div>
                    {(order.status !== 'presupuesto') && (
                      <div className="payment-selector-header">
                        {order.payment_status !== 'paid' && (
                          <div className={`amount-badge pending-amount ${order.payment_details?.method === 'custom' && !isTransferenciaConvenir(order) ? 'cash' : ''}`} style={(order.payment_details?.method === 'wire_transfer' || isTransferenciaConvenir(order)) ? { background: '#1d4ed8', color: '#fff' } : undefined}>
                            {order.payment_details?.method === 'custom' && !isTransferenciaConvenir(order) && <span className="cash-label">EFECTIVO</span>}
                            {(order.payment_details?.method === 'wire_transfer' || isTransferenciaConvenir(order)) && <span className="cash-label" style={{ color: '#dbeafe' }}>TRANSFERENCIA</span>}
                            {(() => {
                              if (order.total_price != null) {
                                return `$${order.total_price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
                              }
                              let subtotal = 0;
                              if (Array.isArray(order.products)) {
                                order.products.forEach((p: any) => {
                                  subtotal += (p.price || 0) * (p.quantity || 1);
                                });
                              }
                              let total = subtotal;
                              if (order.discount) {
                                const isAbsoluteDiscount = order.source === 'tiendanube' || order.source === 'woocommerce' || (order as any).discount_type === 'fixed';
                                total -= isAbsoluteDiscount ? Math.min(order.discount, total) : (subtotal * order.discount / 100);
                              }
                              return `$${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
                            })()}
                            {Number((order as any).deposit_amount) > 0 && (
                              <span className="cash-label" style={{ display: 'block', marginTop: 2 }}>
                                SEÑA ${Number((order as any).deposit_amount).toLocaleString('es-AR')} · RESTA ${Math.max(0, Number(order.total_price || 0) - Number((order as any).deposit_amount)).toLocaleString('es-AR')}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Canje a creadores: no se cobra ni se factura → en vez del estado de pago, el aviso */}
                        {(order as any).is_canje ? (
                          <div className="source-badge" title="Canje para un creador de contenido: total $0, no suma a facturación ni manda WhatsApp"
                            style={{ background: 'rgba(124,58,237,0.14)', color: '#7c3aed' }}>
                            🎁 CANJE · $0 · no se factura
                          </div>
                        ) : (
                        <select
                          value={order.payment_status === 'paid' ? 'paid' : (order.payment_details?.method === 'custom' ? 'pending_cash' : 'pending')}
                          onChange={(e) => updateOrderPaymentStatus(order.id, e.target.value as any)}
                          className={`payment-select ${order.payment_status === 'paid' ? 'paid' : 'pending'}`}
                        >
                          <option value="pending">PENDIENTE</option>
                          <option value="pending_cash">PENDIENTE (EFECTIVO)</option>
                          <option value="paid">PAGADO</option>
                        </select>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Transferencia a convenir: comprobante entrante + marcar pagada */}
                  {isTransferenciaConvenir(order) && order.payment_status !== 'paid' && (() => {
                    const receipts = getReceiptsForOrder(order);
                    return (
                      <div className="transfer-receipt-block">
                        <div className="transfer-receipt-head">
                          <span className={`transfer-receipt-badge ${receipts.length ? 'received' : 'waiting'}`}>
                            {receipts.length ? '📎 Comprobante recibido' : '⏳ Esperando comprobante'}
                          </span>
                          <span className="transfer-receipt-label">Transferencia a convenir</span>
                        </div>
                        {receipts.length > 0 && (
                          <>
                            <div className="transfer-receipt-media">
                              {receipts.map((r, ri) => (
                                <span key={r.id} style={{ position: 'relative', display: 'inline-block' }}>
                                  {r.media_mime_type?.startsWith('image/') ? (
                                    <a href={r.media_url} target="_blank" rel="noreferrer" title={new Date(r.created_at).toLocaleString('es-AR')}>
                                      <img src={r.media_url} alt="Comprobante" />
                                    </a>
                                  ) : (
                                    <a href={r.media_url} target="_blank" rel="noreferrer" className="transfer-receipt-doc">
                                      📄 Ver comprobante ({new Date(r.created_at).toLocaleDateString('es-AR')})
                                    </a>
                                  )}
                                  <button title="Descargar comprobante"
                                    onClick={() => downloadFile(r.media_url, `comprobante_${getOrderDisplayId(order)}_${ri + 1}`)}
                                    style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', padding: '1px 6px', fontSize: '0.75rem' }}>⬇</button>
                                  <button title="No es un comprobante de pago (foto del producto, de la review, una consulta): deja de mostrarse acá"
                                    onClick={() => markNotReceipt(r.id)}
                                    style={{ position: 'absolute', top: 2, left: 2, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', padding: '1px 6px', fontSize: '0.7rem' }}>✕ No es comprobante</button>
                                </span>
                              ))}
                            </div>
                            <button className="transfer-mark-paid" onClick={() => updateOrderPaymentStatus(order.id, 'paid')}>
                              ✓ Confirmar pago (marcar pagada)
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Comprobantes de pago de la orden: los de WhatsApp (solo transferencias) y los
                      adjuntados a mano. "Adjuntar comprobante de pago" está SIEMPRE (pago en partes,
                      se equivocaron de archivo…). Fotos de reviews y otros adjuntos no van acá. */}
                  {order.status !== 'presupuesto' && !(order as any).is_canje && (() => {
                    const manualReceipts = (((order as any).receipt_urls || []) as string[]);
                    const pd: any = order.payment_details || {};
                    const isTransfer = pd.method === 'wire_transfer'
                      || String(pd.payment_method || '').toLowerCase() === 'transferencia'
                      || (order.gateway_name || '').toLowerCase().includes('transferencia');
                    const waReceipts = getReceiptsForOrder(order);
                    const showAttach = true;
                    // los de WhatsApp se listan acá cuando la orden ya está paga (el bloque
                    // de arriba con la imagen solo se muestra mientras el pago está pendiente)
                    const waHidden = isTransfer && order.payment_status === 'paid' ? waReceipts : [];
                    if (!manualReceipts.length && !showAttach && !waHidden.length) return null;
                    const xBtn: React.CSSProperties = { background: 'none', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', padding: '0 5px', color: 'var(--text-muted)' };
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '6px 0' }}>
                        {waHidden.map((r: any, i: number) => (
                          <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                            <a href={r.media_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }} title={new Date(r.created_at).toLocaleString('es-AR')}>
                              📎 Comprobante WA {i + 1}
                            </a>
                            <button title="Descargar" onClick={() => downloadFile(r.media_url, `comprobante_${getOrderDisplayId(order)}_wa${i + 1}`)}
                              style={xBtn}>⬇</button>
                            <button title="No es un comprobante de pago: deja de mostrarse en la orden (sigue en el chat)" onClick={() => markNotReceipt(r.id)} style={xBtn}>✕</button>
                          </span>
                        ))}
                        {manualReceipts.map((u, i) => (
                          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                            <a href={u} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>📎 Comprobante {i + 1}</a>
                            <button title="Descargar" onClick={() => downloadFile(u, `comprobante_${getOrderDisplayId(order)}_${i + 1}`)}
                              style={xBtn}>⬇</button>
                            <button title="Quitar este comprobante de la orden" onClick={() => removeManualReceipt(order, u)} style={xBtn}>✕</button>
                          </span>
                        ))}
                        {showAttach && (
                          <label style={{ fontSize: '0.76rem', cursor: 'pointer', border: '1px dashed var(--border)', borderRadius: 8, padding: '2px 8px', opacity: 0.75 }}
                            title="Adjuntar un comprobante de pago (imagen o PDF). Se puede sumar aunque la orden ya tenga otro (pago en partes, se mandó por otro lado).">
                            📎 Adjuntar comprobante de pago
                            <input type="file" accept="image/*,application/pdf,.pdf" style={{ display: 'none' }}
                              onChange={e => { const f = e.target.files?.[0]; if (f) attachReceipt(order, f); e.target.value = ''; }} />
                          </label>
                        )}
                      </div>
                    );
                  })()}

                  {/* Shipping / Source Custom Info */}
                  <div className="shipping-info-block">
                    {(order.shipping_option || order.shipping_pickup_details) && (
                      <div className="shipping-info">
                        <Truck size={14} />
                        <span>
                          {order.shipping_option}
                          {order.shipping_pickup_details ? (() => {
                            try {
                              const parsed = JSON.parse(order.shipping_pickup_details);
                              return parsed?.name ? ` (${parsed.name})` : ` (${order.shipping_pickup_details})`;
                            } catch (e) {
                              return ` (${order.shipping_pickup_details})`;
                            }
                          })() : ''}
                        </span>
                      </div>
                    )}
                    {order.delivery_type && (
                      <div className="shipping-info">
                        <Truck size={14} />
                        <span>{order.delivery_type === 'envio' ? `Dirección: ${order.delivery_address || 'Sin dirección'}${order.delivery_unit ? ` · Piso/Depto ${order.delivery_unit}` : ''}` : 'Retiro en Showroom'}</span>
                      </div>
                    )}
                    {(() => {
                      const cust = Number(order.shipping_cost_customer);
                      const own = Number(order.shipping_cost_owner);
                      if (order.shipping_cost_customer != null && cust > 0) {
                        return (
                          <div className="shipping-info" title="Envío que pagó el cliente">
                            <Truck size={14} />
                            <span>Envío: ${cust.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                          </div>
                        );
                      }
                      if (order.shipping_cost_customer != null && cust === 0 && own > 0) {
                        return (
                          <div className="shipping-info" style={{ color: '#16a34a' }} title="Envío gratis por promo — lo absorbe la tienda">
                            <Truck size={14} />
                            <span>Envío gratis (bonificado · costo ${own.toLocaleString('es-AR', { maximumFractionDigits: 0 })})</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {/* Vista rápida del seguimiento (lo que ve el cliente) */}
                    {(order as any).track_token && order.status !== 'cancelada' && (
                      <button
                        className="shipping-info"
                        title="Ver el seguimiento como lo ve el cliente"
                        onClick={(e) => { e.stopPropagation(); setTrackViewOrder(order); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--border)', background: 'transparent', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 600, width: 'fit-content' }}
                      >
                        <Search size={12} /> Seguimiento
                      </button>
                    )}
                    {/* Cabify: logística propia, envío gratis o pedidos manuales del admin (Andreani/correo van por su canal) */}
                    {CABIFY_ENABLED && cabifyQualifies(order) && (() => {
                      const cd = cabifyMap.get(order.id);
                      const busy = cabifyBusyId === order.id;
                      const cbtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--border)', background: 'transparent', borderRadius: 6, padding: '2px 8px', cursor: busy ? 'wait' : 'pointer', color: '#7c3aed', fontWeight: 600, width: 'fit-content', opacity: busy ? 0.7 : 1 };
                      if (cd && cd.status !== 'created') {
                        // Despachado o más allá: chip con el estado en vivo + reimpresión.
                        // Antes acá solo estaba el chip: una vez despachado, reimprimir
                        // obligaba a ir a /cabify. Cabify sigue sirviendo el PDF después
                        // del despacho, así que se puede reimprimir sin salir de la orden.
                        return (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* Varias etiquetas (cajas): un chip por etiqueta con SU estado — viajan por separado (02/11/2026) */}
                            {(cd.parcels.length > 1 ? cd.parcels : [{ parcel_id: '', status: cd.status, label: '' }]).map((pc, i) => (
                              <button key={pc.parcel_id || i} className="shipping-info" style={{ ...cbtn, color: pc.status === 'delivered' ? '#10b981' : '#7c3aed' }}
                                title={pc.label ? `Etiqueta ${pc.label}: ver estado y seguimiento` : 'Ver estado del envío Cabify'}
                                onClick={(e) => { e.stopPropagation(); setCabifyStatusOrder(order); }}>
                                <Truck size={13} /> {pc.label ? <b>{pc.label}</b> : null} {CABIFY_STATE_ES[pc.status] || `Cabify: ${pc.status}`}
                              </button>
                            ))}
                            <button className="shipping-info" style={{ ...cbtn, color: 'var(--text-muted)', padding: '2px 6px' }}
                              disabled={busy}
                              title={cd.parcels.length > 1
                                ? `Reimprimir las ${cd.parcels.length} etiquetas de este pedido`
                                : 'Reimprimir la etiqueta Cabify'}
                              onClick={(e) => { e.stopPropagation(); cabifyPrint(order); }}>
                              {busy ? <Loader2 size={13} className="spin" /> : <Printer size={13} />}
                              {cd.parcels.length > 1 ? ` Etiquetas (${cd.parcels.length})` : ' Etiqueta'}
                            </button>
                            {cd.status !== 'delivered' && (
                              <button className="shipping-info" style={{ ...cbtn, color: 'var(--text-muted)', padding: '2px 6px' }} disabled={busy}
                                title="Otra etiqueta igual: el pedido va en más de una caja"
                                onClick={(e) => { e.stopPropagation(); cabifyAddParcel(order); }}>
                                <Plus size={13} /> Bulto
                              </button>
                            )}
                          </div>
                        );
                      }
                      if (cd) {
                        return (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <button className="shipping-info" style={cbtn} disabled={busy} title="Imprimir la(s) etiqueta(s) Cabify de este pedido"
                              onClick={(e) => { e.stopPropagation(); cabifyPrint(order); }}>
                              {busy ? <Loader2 size={13} className="spin" /> : <Printer size={13} />} Imprimir etiqueta Cabify{cd!.parcels.length > 1 ? ` (×${cd!.parcels.length})` : ''}
                            </button>
                            <button className="shipping-info" style={{ ...cbtn, padding: '2px 6px' }} disabled={busy} title="Otra etiqueta igual: el pedido va en más de una caja"
                              onClick={(e) => { e.stopPropagation(); cabifyAddParcel(order); }}>
                              <Plus size={13} /> Bulto
                            </button>
                                                        <button style={{ ...cbtn, color: '#dc2626', padding: '2px 6px' }} disabled={busy} title="Eliminar etiqueta Cabify"
                              onClick={(e) => { e.stopPropagation(); cabifyDeleteLabel(order); }}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        );
                      }
                      return (
                        <button className="shipping-info" style={cbtn} disabled={busy} title="Generar la etiqueta Cabify (no despacha ni cobra)"
                          onClick={(e) => { e.stopPropagation(); cabifyGenerate(order); }}>
                          {busy ? <Loader2 size={13} className="spin" /> : <Truck size={13} />} Generar etiqueta Cabify
                        </button>
                      );
                    })()}
                    {order.coupon_code && (
                      <div className="shipping-info" title={order.coupon_amount ? `Descuento de cupón: $${order.coupon_amount.toLocaleString('es-AR')}` : 'Cupón aplicado'} style={{ color: '#dc2626' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>🎟️ {order.coupon_code}</span>
                        {order.coupon_amount ? (
                          <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>
                            -${order.coupon_amount.toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>

                  <div className="order-body">
                    <div className="product-list">
                      <h4>Productos</h4>
                      {Array.isArray(order.products) && order.products.length > 0 ? (
                        <ul>
                          {order.products.map((p: any, idx: number) => (
                            <li key={idx} className={`product-item ${p.is_cut ? 'cut' : ''} ${p.is_picked ? 'picked' : ''}`}>
                              <label className="product-checkbox">
                                <input
                                  type="checkbox"
                                  checked={!!p.is_cut}
                                  onChange={() => toggleProductCut(order.id, idx)}
                                />
                                <span className="checkbox-custom"></span>
                              </label>
                              <span className="qty">{p.quantity}x</span>
                              <span className="p-name">{p.name}</span>
                              <button
                                className={`pick-btn ${p.is_picked ? 'picked' : ''}`}
                                onClick={() => toggleProductPicked(order.id, idx)}
                                title={p.is_picked ? 'Desmarcar retirado' : 'Marcar como retirado'}
                              >
                                {p.is_picked ? '✓ Retirado' : 'Retirar'}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="no-products">Sin detalle de productos</p>
                      )}
                    </div>

                    {getMiparedCodes(order).length > 0 && (
                      <div className="mipared-block">
                        {getMiparedCodes(order).map((code) => {
                          const st = miparedCombos[code];
                          const wall = `https://mipared.krilic.com.ar/p/${code}`;
                          return (
                            <div key={code} className="mipared-combo">
                              <div className="mipared-head">
                                <span className="mipared-title">🧱 MiPared · {code}</span>
                                <a className="mipared-link" href={wall} target="_blank" rel="noreferrer">Ver la pared ↗</a>
                                <button
                                  className="mipared-copy"
                                  title="Copiar link de la pared"
                                  onClick={() => { navigator.clipboard?.writeText(wall); showSnackbar('Link de la pared copiado', 'success'); }}
                                >Copiar</button>
                              </div>
                              {st?.loading && <div className="mipared-status">Cargando despiece…</div>}
                              {st?.error && <div className="mipared-status err">No se pudo cargar el despiece ({st.error})</div>}
                              {st?.snapshot && (
                                <a href={wall} target="_blank" rel="noreferrer" title="Ver la pared">
                                  <img className="mipared-snap" src={st.snapshot} alt={`Diseño MiPared ${code}`} />
                                </a>
                              )}
                              {st?.combo && (
                                <ul className="mipared-items">
                                  {(st.combo.items || []).map((it: any, i: number) => (
                                    <li key={i}>
                                      <span className="mp-qty">{it.qty}×</span>
                                      <span className="mp-name">{it.productName || it.productId}</span>
                                      {it.sizeLabel ? <span className="mp-attr">{it.sizeLabel}</span> : null}
                                      {(it.customWcm && it.customHcm) ? <span className="mp-attr">{it.customWcm}×{it.customHcm}cm</span> : null}
                                      {it.colorLabel ? <span className="mp-attr">{it.colorLabel}</span> : null}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="order-notes">
                      {order.note && (
                        <div className="note-item customer-note">
                          <MessageSquare size={14} />
                          <p><strong>Cliente:</strong> {order.note}</p>
                        </div>
                      )}

                      <div className="note-item owner-note">
                        <div className="note-header">
                          <div className="note-title">
                            <Info size={14} />
                            <strong>Nota interna:</strong>
                          </div>
                          {editingNoteId !== order.id && (
                            <button className="edit-note-btn" onClick={() => {
                              setEditingNoteId(order.id);
                              setTempNote(order.owner_note || '');
                            }}>
                              <RefreshCw size={12} /> {/* Reusing RefreshCw as a placeholder for edit/swap if no Edit icon */}
                            </button>
                          )}
                        </div>

                        {editingNoteId === order.id ? (
                          <div className="note-editor">
                            <textarea
                              value={tempNote}
                              onChange={(e) => setTempNote(e.target.value)}
                              placeholder="Escribe una nota interna..."
                              autoFocus
                            />
                            <div className="note-editor-actions">
                              <button className="cancel-btn" onClick={() => setEditingNoteId(null)}>
                                <X size={14} />
                              </button>
                              <button className="save-btn" onClick={() => updateOrderInternalNote(order.id, tempNote)}>
                                <CheckCircle size={14} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          order.owner_note ? (
                            <p>{order.owner_note}</p>
                          ) : (
                            <p className="empty-note" onClick={() => {
                              setEditingNoteId(order.id);
                              setTempNote('');
                            }}>+ Agregar nota interna</p>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="order-footer">
                    <div className="status-section">
                      <label className="status-label">Cambiar estado:</label>
                      <div className="status-selector">
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        >
                          {STATUS_OPTIONS.map(status => (
                            <option key={status} value={status}>{STATUS_LABELS[status].toUpperCase()}</option>
                          ))}
                        </select>
                        <ChevronDown size={14} className="select-arrow" />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {order.contact_phone && (
                        <button
                          className="icon-btn-small"
                          onClick={() => openWhatsAppChat(order.contact_phone)}
                          title="Abrir chat de WhatsApp"
                          style={{ padding: '6px', background: '#25D36615', color: '#25D366' }}
                        >
                          <MessageCircle size={18} />
                        </button>
                      )}
                      <button className="icon-btn-small" onClick={() => openWaHistory(order)} title="Historial WhatsApp" style={{ padding: '6px', background: 'var(--bg-secondary)', position: 'relative' }}>
                        <MessageCircle size={18} />
                        {getWaCount(order) > 0 && (
                          <span style={{ position: 'absolute', top: -4, right: -4, background: '#25D366', color: 'white', borderRadius: '999px', minWidth: 16, height: 16, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, boxShadow: '0 0 0 2px var(--card)' }}>
                            {getWaCount(order)}
                          </span>
                        )}
                      </button>
                      <button className="icon-btn-small" onClick={() => openOrderActivity(order)} title="Actividad de la orden" style={{ padding: '6px', background: 'var(--bg-secondary)' }}>
                        <Activity size={18} />
                      </button>
                      <button className="icon-btn-small" onClick={() => generateBudgetPDF(order)} title="Descargar PDF" style={{ padding: '6px', background: 'var(--bg-secondary)' }}>
                        <Download size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Actions Footer - Editable for everything except MercadoLibre */}
                  {order.source !== 'mercadolibre' && (
                    <div className="actions-footer">
                      <button className="action-btn edit-btn" onClick={() => setEditingOrder(order)}>
                        <Info size={16} /> Editar
                      </button>
                      <button className="action-btn delete-btn" onClick={() => deleteOrder(order)}>
                        <Trash2 size={16} /> Eliminar
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="budget-table-container">
            {filteredOrders.length === 0 ? (
              <div className="empty-state">
                <AlertCircle size={48} />
                <p>No hay presupuestos guardados.</p>
              </div>
            ) : (
              <table className="budget-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Origen</th>
                    <th>Total</th>
                    <th style={{ textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => (
                    <tr key={order.id}>
                      <td><span className="order-number">#{getOrderDisplayId(order)}</span></td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>
                        <div className="client-cell">
                          <span className="client-name">{order.contact_name}</span>
                          {order.contact_phone && <span className="client-phone">{order.contact_phone}</span>}
                        </div>
                      </td>
                      <td>
                        {order.source && (
                          <div className={`source-badge ${order.source.toLowerCase()}`} style={{ width: 'fit-content' }}>
                            {order.source === 'tiendanube' && 'Tiendanube'}
                            {order.source === 'mercadolibre' && 'ML'}
                            {order.source === 'woocommerce' && 'Boutique'}
                            {order.source === 'whatsapp' && 'WhatsApp'}
                            {order.source === 'ig' && 'Instagram'}
                            {order.source === 'showroom' && 'Showroom'}
                            {!['tiendanube', 'mercadolibre', 'woocommerce', 'whatsapp', 'ig', 'showroom'].includes(order.source) && order.source}
                          </div>
                        )}
                      </td>
                      <td>
                        {/* Calculate Total Logic Reused */}
                        {(() => {
                          let subtotal = 0;
                          if (Array.isArray(order.products)) {
                            order.products.forEach((p: any) => {
                              subtotal += (p.price || 0) * (p.quantity || 1);
                            });
                          }
                          let total = subtotal;
                          if (order.discount) {
                            const isAbsoluteDiscount = order.source === 'tiendanube' || order.source === 'woocommerce' || (order as any).discount_type === 'fixed';
                            total -= isAbsoluteDiscount ? Math.min(order.discount, total) : (subtotal * order.discount / 100);
                          }
                          return `$${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
                        })()}
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button className="icon-btn-small convert-btn" onClick={(e) => { e.stopPropagation(); if (window.confirm(`¿Convertir presupuesto #${getOrderDisplayId(order)} en venta?`)) updateOrderStatus(order.id, 'pendiente'); }} title="Convertir en venta">
                            <CheckCircle size={18} />
                          </button>
                          {order.contact_phone && (
                            <button
                              className="icon-btn-small"
                              onClick={(e) => { e.stopPropagation(); openWhatsAppChat(order.contact_phone); }}
                              title="Abrir chat de WhatsApp"
                              style={{ background: '#25D36615', color: '#25D366' }}
                            >
                              <MessageCircle size={18} />
                            </button>
                          )}
                          <button className="icon-btn-small" onClick={(e) => { e.stopPropagation(); openWaHistory(order); }} title="Historial WhatsApp" style={{ position: 'relative' }}>
                            <MessageCircle size={18} />
                            {getWaCount(order) > 0 && (
                              <span style={{ position: 'absolute', top: -4, right: -4, background: '#25D366', color: 'white', borderRadius: '999px', minWidth: 16, height: 16, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, boxShadow: '0 0 0 2px var(--card)' }}>
                                {getWaCount(order)}
                              </span>
                            )}
                          </button>
                          <button className="icon-btn-small" onClick={(e) => { e.stopPropagation(); openOrderActivity(order); }} title="Actividad de la orden">
                            <Activity size={18} />
                          </button>
                          <button className="icon-btn-small" onClick={(e) => { e.stopPropagation(); generateBudgetPDF(order); }} title="Descargar PDF">
                            <Download size={18} />
                          </button>
                          <button className="icon-btn-small" onClick={(e) => { e.stopPropagation(); setEditingOrder(order); }} title="Editar">
                            <Info size={18} />
                          </button>
                          <button className="icon-btn-small danger" onClick={(e) => { e.stopPropagation(); deleteOrder(order); }} title="Eliminar">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>

      {/* Snackbar Container */}
      <div className="snackbar-container">
        {snackbars.map(snack => (
          <div key={snack.id} className={`snackbar snackbar-${snack.type}`}>
            {snack.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            <span>{snack.message}</span>
            <button className="snackbar-close" onClick={() => dismissSnackbar(snack.id)}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      {(showNewOrder || editingOrder) && (
        <NewOrder
          initialOrder={editingOrder}
          canje={newOrderCanje && !editingOrder}
          onCancel={() => {
            setShowNewOrder(false);
            setNewOrderCanje(false);
            setEditingOrder(null);
          }}
          onSuccess={() => {
            setShowNewOrder(false);
            setNewOrderCanje(false);
            setEditingOrder(null);
            fetchOrders();
          }}
          showSnackbar={showSnackbar}
        />
      )}

      {/* Tiendanube Connect Modal */}
      {showTnConnect && (
        <div className="modal-overlay" onClick={() => setShowTnConnect(false)}>
          <div style={{
            background: 'var(--card)',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '450px',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Conectar Tiendanube</h3>
              <button onClick={() => setShowTnConnect(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Pegá el Access Token de tu app de Tiendanube Partners. Lo encontrás en la sección de autenticación al instalar la app.
              </p>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                Store ID
                <input
                  type="text"
                  value={tnStoreId}
                  onChange={e => setTnStoreId(e.target.value)}
                  placeholder="Ej: 4010714"
                  style={{ padding: '10px 14px', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '0.9rem', background: 'var(--bg-secondary)', color: 'var(--text)', outline: 'none' }}
                />
              </label>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                Access Token
                <input
                  type="password"
                  value={tnToken}
                  onChange={e => setTnToken(e.target.value)}
                  placeholder="Pegá el token acá"
                  style={{ padding: '10px 14px', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '0.9rem', background: 'var(--bg-secondary)', color: 'var(--text)', outline: 'none', fontFamily: 'monospace' }}
                />
              </label>
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowTnConnect(false)} style={{ padding: '9px 18px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', borderRadius: '10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                Cancelar
              </button>
              <button onClick={saveTnConfig} disabled={tnSaving} style={{ padding: '9px 22px', background: '#0090d0', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', opacity: tnSaving ? 0.6 : 1 }}>
                {tnSaving ? 'Guardando...' : 'Conectar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MÓDULO CABIFY (encapsulado): batch de empaquetados */}
      {showCabifyBatch && (
        <CabifyBatch
          orders={orders
            .filter(o => o.status === 'empaquetado' && cabifyQualifies(o))
            .map(o => ({ id: o.id, displayId: getOrderDisplayId(o), contact_name: o.contact_name, contact_phone: o.contact_phone, delivery_address: o.delivery_address, delivery_unit: o.delivery_unit, delivery_lat: (o as any).delivery_lat, delivery_lon: (o as any).delivery_lon }))}
          userEmail={session?.user?.email}
          onClose={() => { setShowCabifyBatch(false); loadCabifyMap(); }}
        />
      )}

      {/* Popup de seguimiento: iframe de la página pública, exactamente lo que ve el cliente */}
      {trackViewOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => setTrackViewOrder(null)}>
          <div style={{ background: 'var(--card)', borderRadius: 16, width: '100%', maxWidth: 460, height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <b style={{ fontSize: '0.95rem' }}>Seguimiento · {getOrderDisplayId(trackViewOrder)}</b>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button
                  title="Copiar link del cliente"
                  onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/seguimiento/${(trackViewOrder as any).track_token}`); showSnackbar('Link de seguimiento copiado', 'success'); }}
                  style={{ border: '1px solid var(--border)', background: 'transparent', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: 'var(--text)', fontSize: 12, fontWeight: 600 }}
                >Copiar link</button>
                <a
                  href={`/seguimiento/${(trackViewOrder as any).track_token}`} target="_blank" rel="noopener noreferrer"
                  style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', color: 'var(--text)', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
                >Abrir</a>
                <button onClick={() => setTrackViewOrder(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text)', padding: 4 }}><X size={18} /></button>
              </div>
            </div>
            <iframe
              src={`/seguimiento/${(trackViewOrder as any).track_token}`}
              title="Seguimiento del pedido"
              style={{ border: 0, flex: 1, width: '100%', background: '#fff' }}
            />
          </div>
        </div>
      )}

      {/* MÓDULO CABIFY (encapsulado): popup de estado del envío */}
      {cabifyStatusOrder && (
        <CabifyStatusModal
          orderId={cabifyStatusOrder.id}
          displayId={getOrderDisplayId(cabifyStatusOrder)}
          onClose={() => { setCabifyStatusOrder(null); loadCabifyMap(); }}
        />
      )}

      {/* WhatsApp History Modal */}
      {waHistoryOrder && (
        <div className="modal-overlay" onClick={() => setWaHistoryOrder(null)}>
          <div style={{
            background: 'var(--card)',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
                  WhatsApp - #{getOrderDisplayId(waHistoryOrder)}
                </h3>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {waHistoryOrder.contact_name} {waHistoryOrder.contact_phone ? `· ${waHistoryOrder.contact_phone}` : ''}
                </span>
              </div>
              <button onClick={() => setWaHistoryOrder(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', borderRadius: '6px' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{
              padding: '16px 20px',
              overflowY: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
              {waHistoryLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                  <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 8px' }} />
                  <p>Cargando...</p>
                </div>
              ) : waHistoryLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                  <MessageCircle size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
                  <p style={{ margin: 0 }}>No hay mensajes de WhatsApp para esta orden</p>
                </div>
              ) : (
                waHistoryLogs.map((log: any) => {
                  const isOutgoing = log.direction === 'outgoing';
                  const isFailed = log.status === 'failed';
                  return (
                    <div key={log.id} style={{
                      alignSelf: isOutgoing ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                    }}>
                      <div style={{
                        padding: '8px 12px',
                        borderRadius: isOutgoing ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                        background: isFailed ? 'var(--error)' : isOutgoing ? '#dcf8c6' : 'var(--bg-secondary)',
                        color: isFailed ? 'white' : isOutgoing ? '#111' : 'var(--text)',
                        fontSize: '0.82rem',
                        lineHeight: 1.4,
                        border: isFailed ? 'none' : '1px solid var(--border)',
                      }}>
                        {log.template_name && (
                          <div style={{ fontSize: '0.72rem', fontWeight: 600, marginBottom: '4px', opacity: 0.7 }}>
                            {log.template_name}
                          </div>
                        )}
                        {log.media_url && (
                          <div style={{ marginBottom: 6 }}>
                            {(log.media_mime_type || '').startsWith('image/') ? (
                              <a href={log.media_url} target="_blank" rel="noreferrer">
                                <img src={log.media_url} alt="Imagen" style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, display: 'block' }} />
                              </a>
                            ) : (log.media_mime_type || '').startsWith('audio/') ? (
                              <audio controls src={log.media_url} style={{ maxWidth: 220, display: 'block' }} />
                            ) : (log.media_mime_type || '').startsWith('video/') ? (
                              <video controls src={log.media_url} style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, display: 'block' }} />
                            ) : (
                              <a href={log.media_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.72rem' }}>📎 Descargar adjunto</a>
                            )}
                          </div>
                        )}
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {(log.template_name && waTemplateDisplay(log.template_name, log.message_text, log.contact_name))
                            || log.message_text || (isOutgoing ? 'Plantilla enviada' : 'Mensaje recibido')}
                        </div>
                        {log.template_name && (
                          <div style={{ marginTop: 4 }}>
                            <span style={{ fontSize: '0.62rem', fontWeight: 700, background: 'rgba(124,58,237,0.15)', color: '#7c3aed', borderRadius: 5, padding: '1px 7px' }}>📄 {log.template_name}</span>
                          </div>
                        )}
                        {isFailed && log.error_message && (
                          <div style={{ fontSize: '0.72rem', marginTop: '4px', opacity: 0.8 }}>
                            Error: {log.error_message}
                          </div>
                        )}
                        <div style={{
                          fontSize: '0.68rem',
                          textAlign: 'right',
                          marginTop: '4px',
                          opacity: 0.6,
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: '4px',
                        }}>
                          {new Date(log.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          {isOutgoing && (
                            isFailed
                              ? <AlertCircle size={10} />
                              : <CheckCircle size={10} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {activityOrder && (
        <div className="modal-overlay" onClick={() => setActivityOrder(null)}>
          <div style={{
            background: 'var(--card)',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '520px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Activity size={18} /> Actividad - #{getOrderDisplayId(activityOrder)}
                </h3>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {activityOrder.contact_name || 'Sin nombre'}
                </span>
              </div>
              <button onClick={() => setActivityOrder(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', borderRadius: '6px' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{
              padding: '12px 20px 20px',
              overflowY: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
              {activityLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                  <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 8px' }} />
                  <p>Cargando...</p>
                </div>
              ) : activityLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                  <Activity size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
                  <p style={{ margin: 0 }}>No hay actividad registrada para esta orden</p>
                </div>
              ) : (
                activityLogs.map((log: any) => {
                  const d = log.details || {};
                  const when = new Date(log.created_at).toLocaleString('es-AR', {
                    day: '2-digit', month: '2-digit', year: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  });
                  let title = log.action.replace(/_/g, ' ');
                  let body: React.ReactNode = null;
                  switch (log.action) {
                    case 'CAMBIO_ESTADO':
                    case 'CAMBIO_ESTADO_MOVIDO':
                      title = 'Cambio de estado';
                      body = <span>{d.old_status || '—'} → <strong>{d.new_status || '—'}</strong></span>;
                      break;
                    case 'CAMBIO_PAGO':
                      title = 'Pago actualizado';
                      body = <span>{d.new_payment_status === 'paid' ? 'PAGADO' : 'PENDIENTE'}</span>;
                      break;
                    case 'NOTA_INTERNA':
                      title = 'Nota interna';
                      body = <em style={{ color: 'var(--text-muted)' }}>"{d.note}"</em>;
                      break;
                    case 'PRODUCTO_CORTADO':
                      title = d.is_cut ? 'Producto cortado' : 'Producto descortado';
                      body = <span>{d.product_name || 'Producto'}</span>;
                      break;
                    case 'ORDEN_CORTADA_AUTO':
                      title = 'Orden cortada automáticamente';
                      break;
                    case 'ELIMINAR_ORDEN':
                      title = d.soft ? 'Movida a papelera' : 'Eliminada';
                      break;
                    case 'RESTAURAR_ORDEN':
                      title = 'Restaurada de papelera';
                      break;
                    case 'ELIMINAR_DEFINITIVO_ORDEN':
                      title = 'Eliminada definitivamente';
                      break;
                    case 'EDITAR_ORDEN_TN': {
                      title = 'Editada (Tiendanube)';
                      const changes = d.changes || {};
                      const FIELD_LABELS: Record<string, string> = {
                        contact_name: 'Nombre', contact_phone: 'Teléfono', discount: 'Descuento',
                        delivery_type: 'Tipo entrega', delivery_address: 'Dirección', note: 'Nota',
                        total_price: 'Total', products: 'Items',
                      };
                      const fmtVal = (v: any) => {
                        if (v === null || v === undefined || v === '') return '—';
                        if (Array.isArray(v)) return v.map((p: any) => `${p.quantity}× ${p.name} ($${p.price})`).join(', ');
                        return String(v);
                      };
                      body = (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          {Object.entries(changes).map(([k, val]: [string, any]) => (
                            <div key={k} style={{ fontSize: '0.78rem' }}>
                              <strong>{FIELD_LABELS[k] || k}:</strong>{' '}
                              <span style={{ color: 'var(--text-muted)', textDecoration: 'line-through' }}>{fmtVal(val.before)}</span>
                              {' → '}
                              <span>{fmtVal(val.after)}</span>
                            </div>
                          ))}
                        </div>
                      );
                      break;
                    }
                    default:
                      body = <pre style={{ margin: 0, fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>{JSON.stringify(d, null, 2)}</pre>;
                  }
                  return (
                    <div key={log.id} style={{
                      padding: '10px 12px',
                      borderRadius: '10px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border)',
                      fontSize: '0.85rem',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: body ? '4px' : 0 }}>
                        <strong style={{ fontSize: '0.85rem' }}>{title}</strong>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{when}</span>
                      </div>
                      {body && <div style={{ fontSize: '0.82rem', color: 'var(--text)' }}>{body}</div>}
                      {log.user_email && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                          {log.user_email}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </AppSidebar>
  );
}

export default App;

// Buscador de Órdenes/Presupuestos (21/10/2026). El texto tipeado es estado LOCAL: así cada tecla
// re-renderiza solo este input y no todo el admin (con cientos de cards se trababa). Busca con
// Enter; Escape o ✕ limpian. Si la búsqueda aplicada cambia desde afuera (Limpiar filtros), se sincroniza.
function OrderSearchBox({ term, onApply, onClear, placeholder }: {
  term: string; onApply: (v: string) => void; onClear: () => void; placeholder: string;
}) {
  const [text, setText] = useState(term);
  useEffect(() => { setText(term); }, [term]);
  const pending = text.trim() !== term;
  return (
    <div className="flex h-10 min-w-[16rem] flex-1 items-center gap-2 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-3 focus-within:border-[var(--foreground)] lg:max-w-[28rem]">
      <Search size={16} className="shrink-0 text-faint" />
      <input type="text" value={text} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onApply(text); if (e.key === 'Escape') { setText(''); onClear(); } }}
        placeholder={placeholder}
        className="h-full min-w-0 flex-1 bg-transparent text-[length:var(--text-sm)] text-ink placeholder:text-faint focus:outline-none" />
      {pending && text.trim() && (
        <button type="button" onClick={() => onApply(text)} title="Buscar (Enter)"
          className="shrink-0 rounded-[var(--radius-control)] px-2 py-0.5 text-[length:var(--text-xs)] font-medium text-soft hover:bg-muted hover:text-ink">Enter ↵</button>
      )}
      {(text || term) && (
        <button type="button" aria-label="Borrar búsqueda" onClick={() => { setText(''); onClear(); }}
          className="grid h-6 w-6 place-items-center rounded-[var(--radius-control)] text-faint hover:bg-muted hover:text-ink"><X size={14} /></button>
      )}
    </div>
  );
}
